import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import Helmet from 'react-helmet';
import {
  Button, AvonContainer, useError,
} from '@avon/component-library';
import {
  func, bool, node, string,
} from 'prop-types';
import Box from '@material-ui/core/Box';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import useTheme from '@material-ui/styles/useTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Formik, Field } from 'formik';
import loadImage from 'blueimp-load-image';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';
import MuiButton from '@material-ui/core/Button';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Switch from '@material-ui/core/Switch';
import { MoreVert as MoreVertIcon } from '@material-ui/icons';
import withStyles from '@material-ui/core/styles/withStyles';
import CircularProgress from '@material-ui/core/CircularProgress';
import { isEqual } from 'lodash';
import DatePicker from '@avon/component-library/dist/DatePicker';
import { compareAsc } from 'date-fns';
import moment from 'moment';
import Editor from './components/Editor';
import AvatarHeader from './components/AvatarHeader';
import Modal from './components/Modal';
import NewModal from './components/NewModal';
import {
  InputField, SocialList, Heading, AvonCheckGroup, Dropdown,
} from './components/Fields';
import { Testimonial } from './components/Testimonial';
import AvonTooltip from './components/AvonTooltip';
import { mapInitialValues, mapSubmitValues } from './utils/mapInitialValues';
import isEmpty from '../utils/isEmpty';
import { validateValues, getValidators } from './utils/formValidation';
import {
  uploadPrpManImage,
  uploadPrpManData,
  switchPrp,
  uploadPrpManPreviewData,
  getPrpManData,
  getPrpManContent,
  deletePrpManImage,
  uploadPrpManPhoneEmail,
} from '../utils/api';
import {
  confirmIcon, errorIcon, infoIcon, warningIcon, lockIcon,
} from './components/svgs';
import { getStringItem } from '../utils/session-storage';
import { accessTokenKey } from '../utils/config';
import noop from '../utils/noop';
import formatDate from '../utils/formatDate/formatDate';

import '../styles/react-date-range/styles.css'; // main style file
import '../styles/react-date-range/theme/default.css'; // date range style file

import { useTracking } from '../analytics/Analytics';
import { isDesktop, isMobile, isTablet } from '../utils/getDeviceType';

import ProspectContext from '../prospect/ProspectContext';
import EditSaveButton from './components/EditSaveButton';

const ContentWrapper = ({ children, fill, ...props }) => (
  <Box py={7} bgcolor={`colors.${fill ? 'lightZircon' : 'white'}`} {...props}>
    <AvonContainer maxWidth="lg">
      {children}
    </AvonContainer>
  </Box>
);

const WebHeading = ({ title, description }) => (
  <Box style={{
    margin: '24px 0 36px',
    fontFamily: 'Montserrat',
  }}
  >
    <Box style={{
      fontWeight: 500,
      fontSize: 24,
      lineHeight: '32px',
      color: '#181818',
    }}
    >
      {title}
    </Box>
    {
      description && (
        <Box style={{
          paddingTop: 24,
          fontSize: 18,
          lineHeight: '26px',
          color: '#181818',
        }}
        >
          {description}
        </Box>
      )
    }
  </Box>
);

WebHeading.propTypes = {
  title: string,
  description: string,
};

const components = {
  InputField,
  SocialList,
  Heading,
  AvonCheckGroup,
  Dropdown,
};

const useStyles = makeStyles(theme => ({
  topbar: e => (e.isWebApp ? {
    zIndex: 102,
    top: 0,
    position: 'sticky',
    '& #prpman-preview-button': {
      wordBreak: 'unset',
    },
  } : {
    zIndex: 102,
    top: 0,
    position: 'sticky',
    '& #admin-prpman-toolbar:after': {
      content: "''",
      width: '100%',
      display: 'inline-block',
    },
    '& #prpman-preview-button': {
      wordBreak: 'unset',
    },
  }),
  toolbar: e => (e.isWebApp ? {
    height: 58,
    display: 'flex',
    // flexDirection: 'row-reverse',
    // justifyContent: 'flex-end',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 30px',
    backgroundColor: theme.palette.colors.white,
    borderBottom: '4px solid #F6F9F9',
  } : {
    height: 58,
    padding: '0px 18px',
    zIndex: 102,
    width: '100%',
    textAlign: 'justify',
    textAlignLast: 'justify',
    paddingTop: theme.spacing(2.5),
    boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
    backgroundColor: theme.palette.colors.white,
    '& span': {
      fontFamily: 'Montserrat',
      fontWeight: 700,
      fontSize: 16,
    },
  }),
  switchWarp: e => (e.isWebApp ? {
    marginLeft: e.language === 'ar' ? 'unset' : 12,
    marginRight: e.language === 'ar' ? 12 : 'unset',
  } : {
    width: '100%',
    paddingTop: theme.spacing(3.5),
    backgroundColor: theme.palette.colors.white,
  }),
  switchLabel1: e => (e.isWebApp ? {
    flex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 56,
    background: '#F6F9F9',
    border: '1px solid #E4EDED',
    borderRadius: 4,
  } : {
    paddingLeft: styleProps => (styleProps.language === 'ar' ? theme.spacing(2) : theme.spacing(3)),
    paddingRight: styleProps => (styleProps.language === 'ar' ? theme.spacing(3) : theme.spacing(2)),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '& > div': {
      fontWeight: 'normal',
      fontFamily: 'Montserrat',
      fontSize: 16,
      wordBreak: 'break-word',
    },
    '& svg': {
      cursor: 'pointer',
    },
  }),
  customTooltip: {
    '& div .MuiTooltip-tooltip': {
      boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.1)',
      border: 'solid 1px #e5eeee',
      backgroundColor: 'white !important',
    },
  },
  gridButton1: {
    minHeight: theme.spacing(9),
    width: 'auto',
    // wordBreak: 'break-word',
    color: theme.palette.colors.textDark,
    padding: 0,
    minWidth: 0,
    '&:hover': {
      background: 'unset',
      boxShadow: 'unset',
    },
  },
  gridButton2: {
    minHeight: theme.spacing(9),
    maxWidth: 265,
    width: 'auto',
    minWidth: 0,
    padding: 0,
    wordBreak: 'break-word',
    color: theme.palette.colors.corePink,
    '&:hover': {
      // backgroundColor: 'rgba(24, 24, 24, 0.08)',
      background: 'unset',
      boxShadow: 'unset',
    },
  },
  gridButton3: {
    minHeight: theme.spacing(9),
    width: 'auto',
    minWidth: 0,
    paddingTop: 0,
    paddingBottom: 0,
    wordBreak: 'break-word',
    color: theme.palette.colors.corePink,
    fontFamily: 'Montserrat-Bold',
    fontSize: 16,
    '&:hover': {
      background: 'unset',
      boxShadow: 'unset',
    },
  },
  preViewLabel: {
    textDecoration: 'underline',
  },
  bannerWarp: {
    width: '100%',
    backgroundColor: theme.palette.colors.white,
    padding: theme.spacing(1.5),
  },
  banner: {
    width: '100%',
    height: '100%',
    backgroundColor: '#1a8282',
    boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.5)',
    '& p': {
      fontWeight: 'normal',
      color: theme.palette.colors.white,
      fontFamily: 'Montserrat-Medium',
      lineHeight: 'normal',
      fontSize: 14,
    },
  },
  bannerIcon: {
    height: '100%',
    lineHeight: `${theme.spacing(9)}px`,
    paddingLeft: styleProps => (styleProps.language === 'ar' ? 'unset' : theme.spacing(3)),
    paddingRight: styleProps => (styleProps.language === 'ar' ? theme.spacing(3) : 'unset'),
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(0.5),
  },
  titleWarp: {
    height: '100%',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingTop: theme.spacing(3.5),
    paddingBottom: theme.spacing(2),
  },
  closeIcon: {
    textAlign: styleProps => (styleProps.language === 'ar' ? 'left' : 'right'),
    height: '100%',
    lineHeight: `${theme.spacing(9)}px`,
    paddingLeft: styleProps => (styleProps.language === 'ar' ? theme.spacing(3) : 'unset'),
    paddingRight: styleProps => (styleProps.language === 'ar' ? 'unset' : theme.spacing(3)),
    paddingTop: theme.spacing(0.5),
  },
  fieldBgColor: {
    backgroundColor: theme.palette.colors.lightZircon,
  },
  fieldWarp: {
    backgroundColor: theme.palette.colors.white,
  },
  oooCalendar: {
    width: '90%',
    border: 'none',
    borderBottom: 'solid 1px #181818',
    fontSize: 16,
    color: '#546264',
    paddingLeft: 6,
    '&:focus': {
      outline: 'none',
    },
    [theme.breakpoints.down('1025')]: {
      width: '100%',
    },
  },
  oooCalendarError: {
    width: '90%',
    border: 'none',
    borderBottom: 'solid 1px #851168',
    fontSize: 16,
    background: 'rgba(133,17,104,0.05)',
    color: '#546264',
    '&:focus': {
      outline: 'none',
    },
    [theme.breakpoints.down('1025')]: {
      width: '100%',
    },
  },
  divider: {
    height: 1,
    background: 'rgba(0, 0, 0, 0.0784314)',
    marginTop: 36,
  },
  webVanityName: {
    width: '100%',
    padding: 24,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& > *': {
      flex: 1,
    },
    '& > :nth-child(2)': {
      marginLeft: p => (p.language === 'ar' ? 'unset' : 24),
      marginRight: p => (p.language === 'ar' ? 24 : 'unset'),
    },
  },
  previewLabel: {
    color: theme.palette.colors.textDark,
    fontFamily: 'Montserrat',
    fontWeight: 700,
    // padding: '0 16px',
    margin: 0,
  },
  menuPaper: {
    width: 'auto',
    top: '48px!important',
  },
  menuList: {
    padding: 0,
    '& > li': {
      minHeight: 45,
      borderBottom: '1px solid rgba(0, 0, 0, 0.0784314)',
    },
  },
}));

const IOSSwitch = withStyles(theme => ({
  root: {
    width: 42,
    height: 26,
    padding: 0,
    margin: theme.spacing(1),
  },
  switchBase: {
    padding: 1,
    '&$checked': {
      transform: 'translateX(16px)',
      color: theme.palette.common.white,
      '& + $track': {
        backgroundColor: '#1A8282',
        opacity: 1,
        border: 'none',
      },
    },
    '&$focusVisible $thumb': {
      color: '#e4eded',
      border: '1px solid #fff',
    },
  },
  thumb: {
    width: 24,
    height: 24,
  },
  track: {
    borderRadius: 26 / 2,
    border: `0px solid ${theme.palette.grey[400]}`,
    backgroundColor: '#e4eded',
    opacity: 1,
    transition: theme.transitions.create(['background-color', 'border']),
  },
  checked: {},
  focusVisible: {},
}))(({ classes, ...props }) => (
  <Switch
    focusVisibleClassName={classes.focusVisible}
    disableRipple
    classes={{
      root: classes.root,
      switchBase: classes.switchBase,
      thumb: classes.thumb,
      track: classes.track,
      checked: classes.checked,
    }}
    {...props}
  />
));

const avonLogo = (
  <svg width="108" height="26" viewBox="0 0 108 26" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M5.09 24.3602L7.4 19.1002C8 19.0102 8.58 18.9402 9.14 18.9002C9.7 18.8602 10.23 18.8302 10.75 18.8302C11.87 18.8302 12.89 18.9402 13.82 19.1502C14.75 19.3602 15.6 19.7002 16.37 20.1602C17.14 20.6202 17.84 21.1902 18.48 21.8902C19.12 22.5802 19.71 23.4102 20.27 24.3702H25.36L14.44 0.900234H10.92L0 24.3602H5.09ZM12.7 7.07023L16.92 16.3902C16.29 15.9402 15.51 15.6002 14.57 15.3502C13.63 15.1002 12.51 14.9802 11.22 14.9802C10.59 14.9802 9.91 15.0102 9.18 15.0802L12.7 7.07023ZM38.87 24.3602L49.7 0.900234H44.34L37.24 17.1502L30.16 0.900234H24.7L35.36 24.3602H38.88H38.87ZM65.51 25.2602C67.36 25.2602 69.08 24.9402 70.67 24.3002C72.26 23.6602 73.63 22.7802 74.79 21.6402C75.95 20.5002 76.86 19.1602 77.52 17.6202C78.18 16.0802 78.51 14.4002 78.51 12.5902C78.51 10.7802 78.18 9.14023 77.52 7.60023C76.86 6.06023 75.95 4.72023 74.79 3.60023C73.63 2.47023 72.25 1.59023 70.67 0.950234C69.08 0.310234 67.36 -0.00976562 65.51 -0.00976562C63.66 -0.00976562 61.94 0.310234 60.35 0.950234C58.76 1.59023 57.39 2.47023 56.23 3.60023C55.07 4.73023 54.16 6.06023 53.5 7.60023C52.84 9.14023 52.51 10.8102 52.51 12.5902C52.51 14.3702 52.84 16.0702 53.5 17.6202C54.16 19.1602 55.07 20.5002 56.23 21.6402C57.39 22.7802 58.77 23.6702 60.35 24.3002C61.94 24.9402 63.66 25.2602 65.51 25.2602ZM65.51 20.9002C64.35 20.9002 63.28 20.6902 62.31 20.2802C61.34 19.8702 60.49 19.2902 59.78 18.5502C59.06 17.8102 58.51 16.9302 58.1 15.9002C57.7 14.8702 57.5 13.7602 57.5 12.5502C57.5 11.3402 57.7 10.2702 58.1 9.27024C58.5 8.27024 59.06 7.40023 59.78 6.67023C60.49 5.94023 61.34 5.37023 62.31 4.96023C63.28 4.55023 64.35 4.34023 65.51 4.34023C66.67 4.34023 67.74 4.55023 68.71 4.96023C69.68 5.37023 70.52 5.94023 71.24 6.67023C71.95 7.40023 72.51 8.26024 72.92 9.27024C73.33 10.2802 73.52 11.3702 73.52 12.5502C73.52 13.7302 73.32 14.9002 72.92 15.9202C72.52 16.9402 71.96 17.8102 71.24 18.5502C70.52 19.2902 69.68 19.8602 68.71 20.2802C67.74 20.6902 66.67 20.9002 65.51 20.9002ZM85.79 0.900234V24.3602H90.62V9.45023L103.25 24.3602H107V0.900234H102.17V15.8102L89.54 0.900234H85.79Z" fill="#E5004B" />
    <defs>
      <linearGradient id="paint0_linear_7067:214969" x1="-57.0504" y1="16.9253" x2="-43.9704" y2="66.6935" gradientUnits="userSpaceOnUse">
        <stop stopColor="#7F28C4" />
        <stop offset="0.644215" stopColor="#E2197C" />
        <stop offset="1" stopColor="#E5231B" />
      </linearGradient>
    </defs>
  </svg>
);

const justineLogo = (
  <svg width="141" height="39" viewBox="0 0 141 58" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M139.723 32.0702C130.364 40.609 126.439 40.2476 123.416 38.686C122.129 38.0815 121.831 35.9674 123.615 34.6595C132.222 29.0855 134.084 18.0241 123.283 25.0565C121.001 26.598 117.865 30.0873 116.612 36.3714C107.908 39.9005 107.607 37.2619 108.593 32.172C111.224 24.3497 109.31 23.4361 102.855 29.4278C101.444 27.9519 100.357 28.0712 98.6341 29.7709L91.2069 35.5148C84.1317 40.1167 82.8195 39.049 86.4054 32.0462C89.832 26.7848 89.3294 24.7377 83.7787 30.0285C83.2787 30.9249 82.8209 31.7726 82.3928 32.5848C67.8998 46.1704 69.6049 38.3271 80.307 18.7961C106.334 17.6777 134.945 16.6457 134.945 16.6457L136.272 13.4037C136.272 13.4037 107.477 13.8772 84.6222 14.5093C81.1618 14.7658 82.2049 14.5131 84.0188 10.9075C86.1615 6.58314 84.7675 5.79074 82.4279 7.30718C81.0715 8.02606 80.6222 9.74727 79.7267 11.6795C79.1085 13.4073 78.4338 14.2645 77.7012 14.2514L62.7163 14.671L60.6522 19.3107C67.591 18.349 72.0566 18.2621 76.3561 18.5938L67.9111 36.5435C64.1244 46.9961 68.3585 48.9594 79.8419 37.9208C76.2598 46.6102 80.1566 46.5274 91.5441 37.6602C94.5936 35.3027 95.3503 35.8184 93.8229 39.2015C92.9949 41.5168 94.0189 44.0574 96.0926 41.8403L98.9659 37.944C102.174 34.0844 102.582 33.9281 102.498 38.5572C102.805 43.3791 107.303 44.5925 113.322 41.3439C115.569 40.0894 116.375 39.2396 117.643 40.9853C123.313 46.5868 133.311 43.6449 140.485 33.1844C141.118 31.5389 140.864 31.1662 139.723 32.0702ZM122.351 30.0285L123.872 27.2848C124.884 25.8269 126.235 27.1127 125.391 29.1705L124.545 30.285C123.028 32.9426 121.733 32.3999 122.351 30.0285ZM58.178 24.9582C56.5147 26.9923 55.9861 28.2719 55.9151 29.443C55.7266 30.4661 56.1403 30.1516 54.8144 31.0518L51.3283 33.579C41.3425 41.3134 40.5522 38.0537 48.2933 26.0164C49.4623 23.6425 44.4906 25.5165 41.5567 28.5689C41.1318 31.2212 39.8381 33.4499 37.673 35.2588C33.6939 39.2015 31.4955 38.8739 33.6939 34.4003L36.66 28.0557C38.3544 24.6301 37.5047 24.2718 33.525 26.8556C31.2127 28.243 31.1551 28.5458 29.9807 31.6568L26.2657 40.5736C25.0087 44.126 26.7391 44.697 30.2456 42.6322L34.4657 40.06C37.3283 37.7101 38.4533 37.9391 37.8424 40.7454C37.7658 44.1277 41.6207 44.6371 45.7762 42.1178L51.8802 36.1237C54.1435 33.8505 56.0681 32.953 57.6157 34.5639C59.3905 35.9654 60.255 37.5184 60.6319 38.8586C61.1833 40.228 59.9955 42.2014 58.4378 40.5736C58.2451 38.9259 57.3272 38.9441 55.7363 40.2318C54.7351 40.9167 54.0263 41.5005 54.0472 43.1457C54.3467 46.0008 56.5989 45.8859 60.8008 42.8043C67.7478 37.6504 68.649 33.4774 63.5025 30.285C61.3095 28.9627 60.9753 28.109 63.3331 26.1683C66.4858 23.4557 63.2294 19.98 58.178 24.9582ZM39.0806 0L33.1594 0.697634C31.8703 0.885793 31.0741 0.944066 30.8509 2.51115C25.4478 24.4782 23.1757 44.1464 0 58C29.4829 49.9965 39.0806 0 39.0806 0Z" fill="#F37331" />
  </svg>
);

const PrpMan = ({
  callApi, onComplete, language, market, backToEdit,
}) => {
  const isWebApp = sessionStorage.getItem('isWebApp') && !isMobile();
  const [content, setContent] = useState({
    // eslint-disable-next-line
    fields: {}, validators: {}, types: {}, labels: {}, modals: {}, showHideMap: {},
    testimonialContent: {
      heading: 'My team says*',
      subHeading: 'My team says*',
      listHeading: 'Your testimonials',
    },
    takePicture: '',
    uploadPicture: '',
    deletePicture: '',
    dragPicture: '',
    NoCameraFoundModal: '',
    allowCameraModal: '',
    saveChanges: 'SAVE CHANGES',
    selectDateLabel: 'Select Date',
    previewEditLabel: 'Preview',
    selectDateDesc: 'You can select a single date or an interval',
  });
  const theme = useTheme();
  const inputRef = useRef(null);
  const styleProps = { language, isWebApp };
  const classes = useStyles(styleProps);
  const [initialData, setInitialData] = useState();
  let storedDataJson = sessionStorage.getItem('storedData');
  let storedData = storedDataJson ? JSON.parse(storedDataJson) : null;
  // const [originalImageUrl, setOriginalImageUrl] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [editorImage, setEditorImage] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [modalSubmit, setSubmitModal] = useState(false);
  const [modalBack, setModalBack] = useState(false);
  const [dateErrorModal, setDataErrorModal] = useState(false);
  const [imageBlob, setImageBlob] = useState();
  const [vanityNameString, setVanityName] = useState('');
  const [vanityNameOriginal, setVanityNameOriginal] = useState('');
  const [isSubmitting, setSubmitting] = useState(false);
  const [isPreviewSubmitting, setPreviewSubmitting] = useState(false);
  const [locationShowBool, setLocationShow] = useState(false);
  const [phoneShowBool, setPhoneShow] = useState(false);
  const [switchDisable, setSwitchDisable] = useState(false);
  const [oooDisable, setOooDisable] = useState(true);
  const [oooStart, setOooStart] = useState();
  const [oooEnd, setOooEnd] = useState();
  const [initOooDisable, setInitOooDisable] = useState(true);
  const [initOooStart, setInitOooStart] = useState();
  const [initOooEnd, setInitOooEnd] = useState();
  const [oooDateRangeLabel, setOooDateRangeLabel] = useState();
  const [hasProfile, setHasProfile] = useState(false);
  const [emailShowBool, setEmailShow] = useState(false);
  const [aboutMeShowBool, setAboutMeShow] = useState(false);
  const [introShowBool, setIntroShow] = useState(false);
  const [previewShow, setPreviewShow] = useState(true);
  const [bannerShow1, setBannerShow1] = useState({
    open: false,
    content: '',
  });
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [enableModalOpen, setEnableModalOpen] = useState(false);
  const [toolBarObject, setToolBar] = useState({});
  const [switchBarObject, setSwitchBar] = useState({});
  const [apiURLLink, setApiURL] = useState('');
  const [relVanityName, setRelVanityName] = useState('');
  const [focusOn, setFocusOn] = useState(false);
  const [isAvailableURL, setIsAvailableURL] = useState(false);
  const [suggestList1, setSuggestList1] = useState([]);
  const [dateRangeShow, setDateRangeShow] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [showDateRangeError, setShowDateRangeError] = useState(false);
  const [, setTracking] = useTracking();

  const isUpMd = useMediaQuery(theme.breakpoints.up('1024'));

  const [formFields, setFormFields] = useState();
  const [mobileErrorsParent, setMobileErrorsParent] = useState();
  const [submitted, setSubmitted] = useState();
  const [uploadAvatar, setUploadAvatar] = useState(false);

  const previewEditData = sessionStorage.getItem('previewEdit');
  const [previewAvatar, setPreviewAvatar] = useState(previewEditData
    ? JSON.parse(previewEditData).previewAvatar : null);

  const [previewStatus, setPreviewStatus] = useState();
  const [previewAvatarUploaded, setPreviewAvatarUploaded] = useState();
  const [previewAvatarOriUrl, setPreviewAvatarOriUrl] = useState();
  const [editPhone, setEditPhone] = useState(false);
  const [editEmail, setEditEmail] = useState(false);
  const [hasMobileCd, setHasMobileCd] = useState(false);
  const [restoreValue, setRestoreValue] = useState(false);

  const toggleDateRangeShow = () => {
    setDateRangeShow(!dateRangeShow);
  };

  const getAccessToken = () => getStringItem(accessTokenKey) || window._env_.REACT_APP_ACCESS_TOKEN;
  const handleDismiss1 = () => {
    setBannerShow1({
      open: false,
      content: '',
    });
  };
  const progressProps = {
    size: '1em',
    color: 'primary',
  };
  const getChangeContent = (val, handleBlur) => {
    if (val.target.name === 'vanityName') {
      setRelVanityName(val.target.value);
      setFocusOn(false);
      setIsAvailableURL(false);
    }
    handleBlur(val);
  };
  const focusCheck = (val) => {
    if (val.target.name === 'vanityName') {
      setFocusOn(true);
    }
  };
  const iPhone = (method, paramsObject = {}) => {
    const msg = `{"method": "${method}", "params": "${JSON.stringify(paramsObject).replace(/"/g, '\\"')}"}`;
    window
      .webkit
      .messageHandlers
      .avonRec
      .postMessage(msg);
  };
  const Android = (method, andriodObject = {}) => {
    if (window.Android) {
      window.Android.dispatcher(method, JSON.stringify(andriodObject));
    } else {
      noop();
    }
  };
  const backHome = () => {
    setModalBack(false);
    if (sessionStorage.getItem('isWebApp')) {
      window.parent.postMessage('backHome', '*');
      return;
    }
    if (window.navigator.userAgent.match(/(iPhone)|(iPad)|(Macintosh)/i)) {
      iPhone('backHome', {});
    } else {
      Android('backHome', {});
    }
  };

  const closeSubmitModal = () => {
    setSubmitModal(false);
  };

  const { setError, cleanToastBanner } = useError();

  const dateFormatted = (date, formatPattern, lang = 'en') => formatDate(date, lang, formatPattern);

  const isNowAfterDate = date => compareAsc(new Date(), date) === 1;

  useEffect(() => { }, [testimonials]);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpenToolBarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleToolBarClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (market === 'JT') {
      const startDay = document.getElementsByClassName('rdrStartEdge');
      const endDay = document.getElementsByClassName('rdrEndEdge');
      if (startDay[0]) {
        startDay[0].style.backgroundColor = theme.palette.colors.corePink;
      }
      if (endDay[0]) {
        endDay[0].style.backgroundColor = theme.palette.colors.corePink;
      }
      const today = document.getElementsByClassName('rdrDayToday');
      if (today[0]) {
        const todaySpan = today[0].children[1];
        if (todaySpan) {
          const style = document.createElement('style');
          todaySpan.appendChild(style);
          const { sheet } = style;
          sheet.addRule('.rdrDayToday .rdrDayNumber span:after', `content: "";position: absolute;bottom: 0;left: 50%;transform: translate(-50%);width: 18px;height: 2px;border-radius: 2px;background: ${theme.palette.colors.corePink};`);
          sheet.insertRule(`.rdrDayToday .rdrDayNumber span:after{content: "";position: absolute;bottom: 0;left: 50%;transform: translate(-50%);width: 18px;height: 2px;border-radius: 2px;background: ${theme.palette.colors.corePink};}`);
        }
      }
    }
  }, [dateRangeShow, oooStart, oooEnd]);

  useEffect(() => {
    let start = oooStart ? new Date(oooStart) : null;
    let end = oooEnd ? new Date(oooEnd) : null;
    let startFormat = switchBarObject.oooStartDateFormat || 'MMM DD';
    let endFormat = switchBarObject.oooEndDateFormat || 'DD YYYY';
    const wholeFormat = switchBarObject.oooWholeDateFormat || 'MMM DD YYYY';

    let startFormatted;
    let endFormatted;

    // eslint-disable-next-line
    if (start instanceof Date && !isNaN(start.getTime())) {
      if (start === end) {
        startFormat = wholeFormat;
      } else if (start != null && end != null && start.getFullYear() !== end.getFullYear()) {
        startFormat = wholeFormat;
      }
      startFormatted = dateFormatted(start, startFormat, language);
    }
    // eslint-disable-next-line
    if (end instanceof Date && !isNaN(end.getTime())) {
      if (start != null && end != null && start.getMonth() !== end.getMonth()) {
        endFormat = wholeFormat;
      } else if (start != null && end != null && start.getFullYear() !== end.getFullYear()) {
        endFormat = wholeFormat;
      }
      endFormatted = dateFormatted(end, endFormat, language);
    }

    if (start === null) {
      start = switchBarObject.selectDateLabel || 'Select Dates';
    }
    if (end === null) {
      end = switchBarObject.selectDateLabel || 'Select Dates';
    }
    if (start === end) {
      setOooDateRangeLabel(`${startFormatted || (switchBarObject.selectDateLabel || 'Select Dates')}`);
      // eslint-disable-next-line
    } else if (start instanceof Date && !isNaN(start.getTime())
      // eslint-disable-next-line
      && end instanceof Date && !isNaN(end.getTime())
      // eslint-disable-next-line
      && (start.getFullYear() === end.getFullYear && start.getMonth() === end.getMonth() && start.getDate() === end.getDate())) {
      setOooDateRangeLabel(`${dateFormatted(start, wholeFormat, language)}`);
    } else {
      setOooDateRangeLabel(`${startFormatted} - ${endFormatted}`);
    }
  }, [oooStart, oooEnd, oooDisable]);

  useEffect(() => {
    const getStatic = async () => {
      try {
        const res = await callApi(getPrpManContent());
        const {
          modals, fields, labels, apiURL, toolBar, switchBar, showHideMap, testimonialContent,
          saveChanges, selectDateLabel, selectDateDesc, takePicture, uploadPicture, deletePicture,
          dragPicture, NoCameraFoundModal, allowCameraModal, previewEditLabel,
        } = res.jsonObject;
        // const test = res.jsonObject.test.hello.there;
        setFormFields(fields);
        setApiURL(apiURL);
        setToolBar(toolBar);
        setSwitchBar(switchBar);
        const { validators, types } = getValidators(fields);
        setContent({
          testimonialContent,
          fields,
          labels,
          modals,
          validators,
          showHideMap,
          types,
          saveChanges,
          selectDateLabel,
          selectDateDesc,
          takePicture,
          uploadPicture,
          deletePicture,
          dragPicture,
          NoCameraFoundModal,
          allowCameraModal,
          previewEditLabel,
        });
        cleanToastBanner();
      } catch ({ error }) {
        error.reCall = getStatic;
        error.args = [];
        setError(error);
      }
    };
    getStatic();
  }, [callApi]);

  useEffect(() => {
    // eslint-disable-next-line
    const { validators, types } = mobileErrorsParent ? getValidators(formFields, mobileErrorsParent) : getValidators(formFields);
    setContent({ ...content, validators, types });
  }, [formFields, mobileErrorsParent]);

  const updateDateRange = (selection) => {
    const newRange = selection[0];
    setOooStart(newRange.startDate);
    setOooEnd(newRange.endDate);
    setShowDateRangeError(false);
    setPreviewShow(false);
  };

  const getData = useCallback(async (refreshTestimonials) => {
    try {
      setSubmitting(true);
      if (!backToEdit) {
        sessionStorage.removeItem('previewEdit');
      }
      const previewData = sessionStorage.getItem('previewEdit');
      const res = previewData && backToEdit
        ? JSON.parse(previewData)
        : await callApi(getPrpManData());

      const {
        files = {}, status, ...rest
      } = res;
      const {
        // eslint-disable-next-line
        vanityName, locationShow, phoneShow, emailShow, aboutMeShow, introShow,
        suggestList, oooEnabled, testimonial, avatarUploaded, fileIds,
        oooStart: resOooStart, oooEnd: resOooEnd,
      } = res;
      if (!(previewEditData && backToEdit)) {
        sessionStorage.setItem('storedData', JSON.stringify(mapInitialValues(rest, content.fields)));
      }
      if (Object.prototype.hasOwnProperty.call(res, 'mobilePhonNrCd')) {
        setHasMobileCd(true);
      }
      if (sessionStorage.getItem('storedData')) {
        storedDataJson = sessionStorage.getItem('storedData');
        storedData = storedDataJson ? JSON.parse(storedDataJson) : null;
      }
      // if (storedData && res.mobilePhonNr !== storedData.mobilePhonNr) {
      //   setEditPhone(true);
      // }
      // if (storedData && res.emailAddrTxt !== storedData.emailAddrTxt) {
      //   setEditEmail(true);
      // }

      let newTestimonials;
      if (refreshTestimonials) {
        const fetchNewTestimonials = await callApi(getPrpManData());
        const { testimonial: newTestimonial } = fetchNewTestimonials;
        newTestimonials = newTestimonial;
      }

      if (!vanityName && suggestList.length > 0) {
        // setPreviewShow(false);
        setRelVanityName(suggestList[0]);
      }
      setPreviewStatus(status);
      setPreviewAvatarUploaded(avatarUploaded);
      setHasProfile(!!((vanityName && vanityName.length > 0)));
      setLocationShow(locationShow);
      setPhoneShow(phoneShow);
      setEmailShow(emailShow);
      setAboutMeShow(aboutMeShow);
      setIntroShow(introShow);
      if (backToEdit && storedData) {
        // eslint-disable-next-line max-len
        setSwitchDisable((storedData.vanityName && storedData.vanityName.length > 0) ? status : true);
      } else {
        setSwitchDisable((vanityName && vanityName.length > 0) ? status : true);
      }
      setOooDisable(!(oooEnabled === true && !isNowAfterDate(new Date(resOooEnd))));
      setOooStart(resOooStart);
      setOooEnd(resOooEnd);
      setInitOooDisable(!(oooEnabled === true && !isNowAfterDate(new Date(resOooEnd))));
      setInitOooStart(resOooStart);
      setInitOooEnd(resOooEnd);
      setVanityName(vanityName);
      setVanityNameOriginal(vanityName);
      setContent({ ...content, files, fileIds });

      setInitialData(mapInitialValues(rest, content.fields));

      setDataErrorModal(false);
      setSuggestList1(suggestList);
      setTestimonials(refreshTestimonials ? newTestimonials : testimonial);
      setNewImageUrl(files.myPic);
      setPreviewAvatarOriUrl(files.myPic);
      setUploadAvatar(avatarUploaded);
    } catch (error) {
      setDataErrorModal(true);
    }
    setSubmitting(false);
  }, [content.fields]);

  useEffect(() => {
    if (!isEmpty(content.fields) && (submitted || submitted === undefined)) getData();
  }, [callApi, content.fields, submitted]);

  // const closeModal = () => setEditLoading(false);

  const handleCapture = ({ target }) => {
    setEditorImage('');
    setEditLoading(true);
    loadImage(target.files[0], (img) => {
      setEditorImage(img.toDataURL('image/jpeg', 0.5));
    }, { orientation: true });
  };

  const uploadImage = blob => (new Promise((resolve, reject) => {
    const bodyFormData = new FormData();
    const file = new File([blob], 'My Avatar Image', {
      type: 'image/jpeg',
    });
    bodyFormData.append('upfile', file);
    callApi(uploadPrpManImage(bodyFormData)).then((res) => {
      resolve(res.fileId);
    })
      .catch((error) => {
        reject(error);
      });
  }));

  const dataURLtoBlob = (dataurl) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = window.atob(arr[1]);
    let strlen = bstr.length;
    const u8arr = new Uint8Array(strlen);
    // eslint-disable-next-line
    while (strlen--) {
      u8arr[strlen] = bstr.charCodeAt(strlen);
    }
    return new Blob([u8arr], { type: mime });
  };

  const blobToBase64 = blob => new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onload = (e) => {
      resolve(e.target.result);
    };
    // readAsDataURL
    fileReader.readAsDataURL(blob);
    fileReader.onerror = () => {
      reject(new Error('blobToBase64 error'));
    };
  });

  const urlToBase64 = url => new Promise((resolve) => {
    const timeStamp = new Date().getTime();
    const Img = new Image();
    Img.src = `${url}?${timeStamp}`;
    Img.crossOrigin = 'Anonymous';
    const ext = Img.src.substring(Img.src.lastIndexOf('.') + 1).toLowerCase();
    // eslint-disable-next-line
    Img.onload = function () {
      const canvas = document.createElement('canvas');
      canvas.width = Img.width;
      canvas.height = Img.height;
      canvas.getContext('2d').drawImage(Img, 0, 0, Img.width, Img.height);
      resolve(canvas.toDataURL(`image/${ext}`));
    };
  });

  const deleteImage = async () => {
    setNewImageUrl(content.files.defaultPic);
    setPreviewShow(false);
    setImageBlob();
    setUploadAvatar(false);
    setPreviewAvatar(null);
    urlToBase64(content.files.defaultPic).then((res) => {
      if (previewEditData) {
        delete previewEditData.previewAvatar;
        sessionStorage.setItem('previewEdit', JSON.stringify(previewEditData));
      }
      setPreviewAvatar(res);
    });
  };

  const avatarImageUpdate = (blob) => {
    window.URL.revokeObjectURL(newImageUrl);
    const blobUrl = window.URL.createObjectURL(blob);
    setNewImageUrl(blobUrl);
    // setOriginalImageUrl(window.URL.createObjectURL(blob));
    setImageBlob(blob);
    setPreviewShow(false);
    setUploadAvatar(true);
    blobToBase64(blob).then((res) => {
      // update new upload image in the sessionStorage
      if (previewEditData) {
        delete previewEditData.previewAvatar;
        sessionStorage.setItem('previewEdit', JSON.stringify(previewEditData));
      }
      setPreviewAvatar(res);
    });
  };

  const isOOODateValid = () => {
    if (!oooDisable && (oooStart === null || oooStart === false)) {
      setShowDateRangeError(true);
      return false;
    }
    return true;
  };

  const setSpeciList = (values) => {
    let speciListJsonObject = [];
    const speciList = [];
    content.fields.forEach(section => section.forEach(({ type, properties }) => {
      if (type === 'AvonCheckGroup') {
        speciListJsonObject = JSON.parse(JSON.stringify(properties.options));
      }
    }));
    // eslint-disable-next-line
    for (let i = 0; i < values.specilist.length; i++) {
      // eslint-disable-next-line
      for (let j = 0; j < speciListJsonObject.length; j++) {
        // eslint-disable-next-line
        if (values.specilist[i] === speciListJsonObject[j].value) {
          speciList.push(values.specilist[i]);
        }
      }
    }
    // eslint-disable-next-line
    values.specilist = speciList;
  };

  const submitData = async (values) => {
    setEditPhone(false);
    setEditEmail(false);
    setSubmitting(true);
    setSubmitted(false);
    setSpeciList(values);
    const body = {
      profile: mapSubmitValues(values, content.types),
    };

    body.profile.locationShow = locationShowBool;
    body.profile.emailShow = emailShowBool;
    body.profile.phoneShow = phoneShowBool;
    body.profile.aboutMeShow = aboutMeShowBool;
    body.profile.introShow = introShowBool;
    body.profile.status = true;
    body.profile.oooEnabled = !oooDisable;
    body.profile.oooStart = oooDisable ? null : oooStart;
    body.profile.oooEnd = oooDisable ? null : oooEnd;

    if (oooStart) {
      body.profile.oooStartLocal = moment.parseZone(oooStart).local().format('YYYY-MM-DD');
    }
    if (oooEnd) {
      body.profile.oooEndLocal = moment.parseZone(oooEnd).local().format('YYYY-MM-DD');
    }

    if (isOOODateValid === false) return false;

    if (values.updateAgs === 'false') {
      body.profile.updateAgs = false;
    }
    cleanToastBanner();
    setBannerShow1({
      open: false,
      content: toolBarObject.saveSuccessInfo,
    });
    try {
      if (content.fileIds && imageBlob === undefined && !uploadAvatar) {
        await callApi(deletePrpManImage(content.fileIds));
      }
      if (imageBlob) {
        await uploadImage(imageBlob).then((res) => {
          body.profile.fileIds = res;
        });
        window.parent.postMessage({ type: 'uploadImage', url: newImageUrl }, '*');
      }
      // eslint-disable-next-line no-param-reassign
      delete body.profile.emailAddrTxt;
      // // eslint-disable-next-line no-param-reassign
      delete body.profile.mobilePhonNr;

      if (backToEdit && previewAvatar) {
        const avatarBlob = dataURLtoBlob(previewAvatar);
        await uploadImage(avatarBlob).then((res) => {
          body.profile.fileIds = res;
        });
      }
      await callApi(uploadPrpManData(body)).then((res) => {
        setVanityName(res.vanityName);
        setVanityNameOriginal(res.vanityName);
        setInitOooDisable(oooDisable);
        setInitOooStart(oooStart);
        setInitOooEnd(oooEnd);
        setPreviewShow(true);
        // eslint-disable-next-line no-param-reassign
        delete values.updateAgs;
        // setInitialData(values);
        // setOriginalImageUrl('');
        onComplete(res);
      });

      setSubmitting(false);
      setImageBlob();
      setErrorModalOpen(false);
      setEditModalOpen(false);
      cleanToastBanner();
      setBannerShow1({
        open: true,
        content: toolBarObject.saveSuccessInfo,
      });
      setSubmitted(true);
      sessionStorage.removeItem('previewEdit');
      sessionStorage.removeItem('storedData');

      if (sessionStorage.getItem('isWebApp')) {
        window.parent.postMessage('profileUpdated', '*');
      }
      return true;
    } catch ({ error }) {
      closeSubmitModal();
      setModalBack(false);
      error.slReCall = submitData;
      error.slArgs = values;
      error.reCall = submitData;
      error.args = [values];
      setError(error);
      setErrorModalOpen(false);
      setEditModalOpen(false);
      setSubmitting(false);
      setEditPhone(false);
      setEditEmail(false);
      return false;
    }
  };

  const openSubmitModal = (values) => {
    if (isOOODateValid() === true) {
      if (values.vanityName === vanityNameOriginal) {
        submitData(values);
      } else if (vanityNameOriginal === '') {
        setEditModalOpen(true);
      } else {
        setErrorModalOpen(true);
      }
    }
  };

  const openSwitchModal = () => {
    if (switchDisable) {
      setDisableModalOpen(true);
    } else {
      setEnableModalOpen(true);
    }
  };

  const toggleOoo = async () => {
    setOooDisable(!oooDisable);
    setPreviewShow(false);
    if (oooDisable) {
      setOooStart(false);
      setOooEnd(false);
      setOooDateRangeLabel(null);
      setShowDateRangeError(false);
    } else {
      setTracking({ eventType: 'pageview', eventName: 'out_of_office_on' });
    }
  };

  const changeStatus = async () => {
    const body = {
      profile: {
        status: !switchDisable,
        vanityName: vanityNameString,
      },
    };
    try {
      setSubmitting(true);
      await callApi(switchPrp(body)).then(res => onComplete(res));
      if (sessionStorage.getItem('isWebApp')) {
        window.parent.postMessage('profileUpdated', '*');
      }
      setSubmitting(false);
      setDisableModalOpen(false);
      setEnableModalOpen(false);
      setSwitchDisable(!switchDisable);
      cleanToastBanner();
      return true;
    } catch ({ error }) {
      closeSubmitModal();
      error.reCall = changeStatus;
      error.args = [];
      setError(error);
      setSubmitting(false);
      setDisableModalOpen(false);
      setEnableModalOpen(false);
      return false;
    }
  };

  const runValidation = (values) => {
    const validated = validateValues(values, content);
    if (editPhone || editEmail) {
      const reserve = ['mobilePhonNr', 'emailAddrTxt'];
      Object.keys(validated).forEach((key) => {
        if (!reserve.includes(key)) delete validated[key];
      });
    }

    if (!editPhone) {
      delete validated.mobilePhonNr;
    }
    if (!editEmail) {
      delete validated.emailAddrTxt;
    }
    return validated;
  };

  const navigate = async (values) => {
    // store the preview data into DB
    if (values) {
      setSpeciList(values);
      const body = {
        profile: {
          profilePreview: values,
        },
      };
      try {
        setPreviewSubmitting(true);
        const timeSt = new Date().valueOf();
        await callApi(uploadPrpManPreviewData(body)).then(() => {
          // eslint-disable-next-line
          window.location.href = `${toolBarObject.href}?accessToken=${getAccessToken()}&previewData=true${isWebApp ? '' : '&isWebApp=true'}&time=${timeSt}`;
          // window.location.href = `http://localhost:3001/prp/UK/en/product/preview?accessToken=${getAccessToken()}&previewData=true${isWebApp ? '' : '&isWebApp=true'}`;
        });
        // setSubmitting(false);
        cleanToastBanner();
        return true;
      } catch ({ error }) {
        closeSubmitModal();
        setModalBack(false);
        error.slReCall = submitData;
        error.slArgs = values;
        error.reCall = submitData;
        error.args = [values];
        setError(error);
        setErrorModalOpen(false);
        setEditModalOpen(false);
        setPreviewSubmitting(false);
        return false;
      }
    } else {
      // eslint-disable-next-line
      window.location.href = `${toolBarObject.href}?accessToken=${getAccessToken()}${isWebApp ? '' : '&isWebApp=true'}`;
    }
    return true;
  };
  const tipMsg = switchBarObject.tipMsg && switchBarObject.tipMsg.replace('{apiURL}', apiURLLink).replace('{vanityName}', vanityNameString);
  const oooTipMsg = switchBarObject.oooTipMsg || '';
  const description = switchBarObject.description && switchBarObject.description.replace('{apiURL}', apiURLLink).replace('{vanityName}', `<strong style="word-break: break-all">${vanityNameString}</strong>`);
  const iconMap = {
    Information: infoIcon,
    Confirmation: confirmIcon,
    Warning: warningIcon,
    Error: errorIcon,
    Lock: lockIcon,
  };
  switch (language) {
    case 'ar':
      // set direction to rtl
      document.body.dir = 'rtl';
      break;

    case 'ru':
    case 'tr':
    case 'tl':
      // set direction to ltr
      document.body.dir = 'ltr';
      break;

    default:
      // Do nothing
      break;
  }

  const getOffset = (element) => {
    try {
      const ele = element.offsetParent.offsetParent;
      return ele.offsetTop;
    } catch (e) {
      return 0;
    }
  };

  const locateError = (errors, setTouched) => {
    if (errors && Object.keys(errors).length > 0) {
      const firstErrorKey = Object.keys(errors)[0];
      const inputRefTop = inputRef.current.querySelector(`#prpman-AvonInputField-${firstErrorKey}`)
        || inputRef.current.querySelector(`#prpman-AvonInputField-${firstErrorKey}-input`);
      if (inputRefTop) {
        const inputParentTop = getOffset(inputRefTop) - 90;
        const increment = window.scrollY / 20;
        const createScroll = () => setTimeout(() => {
          if (inputParentTop > 0 && window.scrollY > inputParentTop) {
            window.scrollTo({
              top: window.scrollY - increment,
              left: 0,
            });
            createScroll();
          }
        }, 20);
        if (window.scrollY) {
          createScroll();
        }
        setTimeout(() => {
          inputRefTop.focus();
          const temTouch = {};
          Object.keys(errors).forEach((idx) => {
            temTouch[idx] = true;
          });
          setTouched(temTouch);
        }, 500);
      }
    }
  };


  const [modalEdit, setModalEdit] = useState(false);
  const [modalDel, setModalDel] = useState(false);
  const [submitEditForm] = useState(null);
  const [testimonialDel, setTestimonialDel] = useState(null);

  // edit modal
  const closeEditModal = () => {
    setModalEdit(false);
  };

  const onSaveClick = async (fn, values) => {
    // if (showModal) {
    //   setSubmitEditForm({
    //     fn,
    //     values,
    //   });
    //   setModalEdit(true);
    // } else {
    await fn(values);
    // }
  };

  const handleEditFormSubmit = async () => {
    const { fn, values } = submitEditForm;
    await fn(values);
  };

  // del modal
  const closeDelModal = () => {
    setModalDel(false);
  };

  const onDeleteClick = (fn, values) => {
    setTestimonialDel({
      fn,
      values,
    });
    setModalDel(true);
  };

  const handleTestimonialFormDel = () => {
    const { fn, values } = testimonialDel;
    fn(values);
  };

  const wrapPreviewData = values => ({
    ...values,
    locationShow: locationShowBool,
    emailShow: emailShowBool,
    oooEnabled: !oooDisable,
    phoneShow: phoneShowBool,
    aboutMeShow: aboutMeShowBool,
    introShow: introShowBool,
    files: { myPic: previewAvatarOriUrl },
    oooStart: oooDisable ? null : oooStart,
    oooEnd: oooDisable ? null : oooEnd,
    socialSharing: Object.keys(values.socialSharing)
      .map(e => ({ contactType: e, value: values.socialSharing[e] })),
    testimonial: testimonials,
    suggestList: suggestList1,
    status: true,
    avatarUploaded: previewAvatarUploaded,
    previewAvatar,
  });

  const savePhoneEmail = async (values, type) => {
    setSubmitted(false);
    let data = '';
    if (type === 'phone') {
      if (hasMobileCd) {
        data = {
          profile: {
            updatePhone: true,
            // vanityName: values.vanityName,
            mobilePhonNr: values.mobilePhonNr,
            mobilePhonNrCd: values.mobilePhonNrCd,
          },
        };
      } else {
        data = {
          profile: {
            updatePhone: true,
            // vanityName: values.vanityName,
            mobilePhonNr: values.mobilePhonNr,
          },
        };
      }
    }

    if (type === 'email') {
      data = {
        profile: {
          updatePhone: false,
          // vanityName: values.vanityName,
          emailAddrTxt: values.emailAddrTxt,
        },
      };
    }

    if (Object.prototype.hasOwnProperty.call(values, 'updatePhone')) {
      if (values.updatePhone) {
        if (hasMobileCd) {
          data = {
            profile: {
              updatePhone: values.updatePhone,
              // vanityName: values.vanityName,
              mobilePhonNr: values.mobilePhonNr,
              updateAgs: values.updateAgs,
              mobilePhonNrCd: values.mobilePhonNrCd,
            },
          };
        } else {
          data = {
            profile: {
              updatePhone: values.updatePhone,
              // vanityName: values.vanityName,
              mobilePhonNr: values.mobilePhonNr,
              updateAgs: values.updateAgs,
            },
          };
        }
      } else {
        data = {
          profile: {
            updatePhone: values.updatePhone,
            // vanityName: values.vanityName,
            emailAddrTxt: values.emailAddrTxt,
            updateAgs: values.updateAgs,
          },
        };
      }
    }

    if (values.updateAgs === 'false') {
      data.profile.updateAgs = false;
    }
    cleanToastBanner();
    setBannerShow1({
      open: false,
      content: toolBarObject.saveSuccessInfo,
    });

    const isValid = runValidation(data.profile);

    if (data !== '' && Object.keys(isValid).length === 0) {
      try {
        setSubmitting(true);
        await callApi(uploadPrpManPhoneEmail(data)).then((res) => {
          // eslint-disable-next-line no-param-reassign
          delete values.updateAgs;
          // eslint-disable-next-line no-param-reassign
          delete values.updatePhone;
          setInitialData(values);
          onComplete(res);
        });
        setSubmitting(false);
        setErrorModalOpen(false);
        setEditModalOpen(false);
        cleanToastBanner();
        setBannerShow1({
          open: true,
          content: toolBarObject.saveSuccessInfo,
        });
        setSubmitted(true);
        if (sessionStorage.getItem('isWebApp')) {
          window.parent.postMessage('profileUpdated', '*');
        }
        setEditPhone(false);
        setEditEmail(false);
        return true;
      } catch ({ error }) {
        const vlas = JSON.parse(JSON.stringify(values));
        vlas.updatePhone = data.profile.updatePhone;
        delete vlas.vanityName;
        closeSubmitModal();
        setModalBack(false);
        error.slReCall = savePhoneEmail;
        error.slArgs = vlas;
        error.reCall = savePhoneEmail;
        error.args = [vlas];
        setError(error);
        setErrorModalOpen(false);
        setEditModalOpen(false);
        setSubmitting(false);
        if (type === 'phone') {
          setEditPhone(true);
        }
        if (type === 'email') {
          setEditEmail(true);
        }
        return false;
      }
    } else return false;
  };

  const prpmanSave = async (values, errors, submitForm, setTouched) => {
    const tempValues = values;
    const tempErrors = errors;
    const errorsArray = Object.keys(tempErrors);

    delete tempValues.mobilePhonNr;
    delete tempValues.emailAddrTxt;
    setEditPhone(false);
    setEditEmail(false);
    setRestoreValue(true);

    if (hasMobileCd) {
      delete tempValues.mobilePhonNrCd;
    }
    if (errorsArray && errorsArray.includes('emailAddrTxt')) {
      delete tempErrors.emailAddrTxt;
    }
    if (errorsArray && errorsArray.includes('mobilePhonNr')) {
      delete tempErrors.mobilePhonNr;
    }
    if (errorsArray && errorsArray.includes('mobilePhonNrCd')) {
      delete tempErrors.mobilePhonNrCd;
    }
    if (tempErrors && Object.keys(tempErrors).length === 0) {
      submitForm(tempValues);
    } else {
      locateError(tempErrors, setTouched);
    }
  };

  const [isDataChange, setIsDataChange] = useState(false);
  useEffect(() => {
    if (!previewShow) {
      setIsDataChange(true);
    }
  }, [previewShow]);

  const isValuesChanged = (original, updated) => {
    const storedDataWithoutPhoneAndEmail = { ...original };
    delete storedDataWithoutPhoneAndEmail.mobilePhonNr;
    delete storedDataWithoutPhoneAndEmail.emailAddrTxt;

    const valuesWithoutPhoneAndEmail = { ...updated };
    delete valuesWithoutPhoneAndEmail.mobilePhonNr;
    delete valuesWithoutPhoneAndEmail.emailAddrTxt;

    return !isEqual(storedDataWithoutPhoneAndEmail, valuesWithoutPhoneAndEmail);
  };

  const handleClickPreview = (values, errors, setTouched) => {
    setEditPhone(false);
    setEditEmail(false);
    setRestoreValue(true);
    const tempData = values;
    const tempErrors = errors;

    if (tempData.emailAddrTxt) {
      tempData.emailAddrTxt = storedData.emailAddrTxt;
    }
    if (tempData.mobilePhonNr) {
      tempData.mobilePhonNr = storedData.mobilePhonNr;
    }
    if (tempData.mobilePhonNrCd) {
      tempData.mobilePhonNrCd = storedData.mobilePhonNrCd;
    }

    if (tempErrors.emailAddrTxt) {
      delete tempErrors.emailAddrTxt;
    }
    if (tempErrors.mobilePhonNr) {
      delete tempErrors.mobilePhonNr;
    }
    if (tempErrors.mobilePhonNrCd) {
      delete tempErrors.mobilePhonNrCd;
    }

    if (tempErrors && Object.keys(tempErrors).length === 0
    && tempData.surName !== '' && tempData.vanityName !== '' && tempData.name !== '') {
      sessionStorage.setItem('previewEdit', JSON.stringify(wrapPreviewData(tempData)));
      const lastestData = sessionStorage.getItem('storedData') ? JSON.parse(sessionStorage.getItem('storedData')) : null;
      if (lastestData
        && (!isEqual(lastestData, tempData) || previewAvatar)) {
        navigate(wrapPreviewData(tempData));
      } else {
        navigate();
      }
    } else {
      locateError(tempErrors, setTouched);
    }
  };

  return (
    <>
      <ProspectContext.Provider value={{
        inputRef,
        setMobileErrorsParent,
        content,
      }}
      >
        <Helmet htmlAttributes={{ lang: language || 'en' }} />
        {!!initialData && (
          <>
            <Formik
              initialValues={initialData}
              validate={runValidation}
              onSubmit={openSubmitModal}
              validateOnChange
            >
              {({
                submitForm, values, errors, handleBlur, setTouched,
              }) => (
                <form id="admin-prpman-form-1">
                  <Box className={classes.topbar}>
                    <Box
                      data-testid="toolbar"
                      id="admin-prpman-toolbar"
                      component="div"
                      className={classes.toolbar}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <Box id="back-home-box">
                        <MuiButton
                          className={classes.gridButton1}
                          id="back-home-button"
                          onClick={() => {
                            // eslint-disable-next-line
                            if (
                              (
                              // !isEqual(initialData, values)||
                              // eslint-disable-next-line
                              // (backToEdit && ((isValuesChanged(storedData ? storedData : initialData, values)) || previewAvatar))
                              // eslint-disable-next-line
                                ((isValuesChanged(storedData || initialData, values)) || previewAvatar)
                              // || !previewShow
                              || !isEqual(initOooDisable, oooDisable)
                              || !isEqual(initOooStart, oooStart)
                              || !isEqual(initOooEnd, oooEnd)
                              )
                              && switchDisable) {
                              if (isOOODateValid() === false) {
                                return;
                              }
                              setModalBack(true);
                            } else {
                              sessionStorage.removeItem('previewEdit');
                              backHome();
                            }
                          }
                          }
                        >
                          {toolBarObject.close}
                        </MuiButton>
                      </Box>
                      {' '}
                      {
                        isWebApp ? (
                          <Box>
                            {switchDisable && (
                              <>
                                <MuiButton
                                  style={{
                                    whiteSpace: 'nowrap',
                                  }}
                                  whiteSpace="nowrap"
                                  color="secondary"
                                  variant="contained"
                                  className={classes.gridButton2}
                                  onClick={() => {
                                    handleClickPreview(values, errors, setTouched);
                                  }}
                                  id="prpman-preview-edit-button"
                                >
                                  {isPreviewSubmitting
                                    ? (<CircularProgress {...progressProps} />)
                                    : (<Typography class={classes.previewLabel}>{content.previewEditLabel || 'Preview'}</Typography>)}
                                </MuiButton>
                                {
                                  // eslint-disable-next-line max-len
                                  (!previewShow && (isDataChange || previewStatus)) || (backToEdit && sessionStorage.getItem('previewEdit') && previewStatus && (isValuesChanged(storedData, values) || previewAvatar)) ? (
                                    <MuiButton
                                      style={{
                                        whiteSpace: 'nowrap',
                                        paddingLeft: language === 'ar' ? 'unset' : 16,
                                        paddingRight: language === 'ar' ? 16 : 'unset',
                                      }}
                                      whiteSpace="nowrap"
                                      color="secondary"
                                      variant="contained"
                                      className={classes.gridButton2}
                                      onClick={
                                        () => prpmanSave(values, errors, submitForm, setTouched)
                                      }
                                      id="prpman-save-button-web"
                                      // eslint-disable-next-line max-len
                                      disabled={!values.vanityName || !values.name || !values.surName}
                                    >
                                      {content.saveChanges}
                                      {!isSubmitting ? (
                                        <Box ml={language === 'ar' ? 'unset' : 1.5} mr={language === 'ar' ? 1.5 : 'unset'}>
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="17"
                                            height="14"
                                            viewBox="0 0 17 14"
                                          >
                                            <path
                                              fill={!values.vanityName || !values.name || !values.surName ? '#FFFFFF' : theme.palette.colors.corePink}
                                              fillRule="nonzero"
                                              d="M15.293.293l1.414 1.414L5 13.414.293 8.707l1.414-1.414L5 10.586z"
                                            />
                                          </svg>
                                        </Box>
                                      ) : (
                                        <CircularProgress {...progressProps} />
                                      )}
                                    </MuiButton>
                                  ) : null
                                }
                              </>
                            )}
                          </Box>
                        ) : (
                          <Box
                            style={{
                              position: 'absolute',
                              right: language === 'ar' ? 'unset' : 18,
                              left: language === 'ar' ? 18 : 'unset',
                            }}
                          >
                            {
                              // eslint-disable-next-line max-len
                              switchDisable && (!previewShow || (backToEdit && sessionStorage.getItem('previewEdit') && (isValuesChanged(storedData, values) || previewAvatar))) ? (
                                <>
                                  <Box
                                    onClick={handleOpenToolBarClick}
                                    id="prpman-toolbar-3dots-box"
                                  >
                                    <MoreVertIcon />
                                  </Box>
                                  <Menu
                                    id="toolbar-menu"
                                    anchorEl={anchorEl}
                                    keepMounted
                                    open={Boolean(anchorEl)}
                                    onClose={handleToolBarClose}
                                    classes={{
                                      paper: classes.menuPaper,
                                      list: classes.menuList,
                                    }}
                                  >
                                    <MenuItem
                                      id="prpman-preview-edit"
                                      onClick={() => {
                                        handleToolBarClose();
                                        handleClickPreview(values, errors, setTouched);
                                      }}
                                    >
                                      {isPreviewSubmitting
                                        ? (<CircularProgress {...progressProps} />)
                                        : (<Typography class={classes.previewLabel}>{content.previewEditLabel || 'Preview'}</Typography>)}
                                    </MenuItem>
                                    {
                                      // eslint-disable-next-line max-len
                                      switchDisable && (!previewShow || (backToEdit && sessionStorage.getItem('previewEdit') && (isValuesChanged(storedData, values) || previewAvatar))) && (
                                        <MenuItem
                                          id="prpman-save"
                                          onClick={() => {
                                            handleToolBarClose();
                                            prpmanSave(values, errors, submitForm, setTouched);
                                          }}
                                        >
                                          <MuiButton
                                            color="secondary"
                                            variant="contained"
                                            className={classes.gridButton2}
                                            // eslint-disable-next-line max-len
                                            // onClick={() => }
                                            // eslint-disable-next-line max-len
                                            disabled={!values.vanityName || !values.name || !values.surName}
                                            id="prpman-save-button-mobile"
                                          >
                                            {!isSubmitting ? (
                                              <>
                                                {toolBarObject.save}
                                                <Box ml={language === 'ar' ? 'unset' : 1.5} mr={language === 'ar' ? 1.5 : 'unset'}>
                                                  <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="17"
                                                    height="14"
                                                    viewBox="0 0 17 14"
                                                  >
                                                    <path
                                                      fill={!values.vanityName || !values.name || !values.surName ? '#FFFFFF' : theme.palette.colors.corePink}
                                                      fillRule="nonzero"
                                                      d="M15.293.293l1.414 1.414L5 13.414.293 8.707l1.414-1.414L5 10.586z"
                                                    />
                                                  </svg>
                                                </Box>
                                              </>
                                            ) : (
                                              <CircularProgress {...progressProps} />
                                            )}
                                          </MuiButton>
                                        </MenuItem>
                                      )}
                                  </Menu>
                                </>
                              ) : (
                                <MuiButton
                                  style={{
                                    whiteSpace: 'nowrap',
                                  }}
                                  whiteSpace="nowrap"
                                  color="secondary"
                                  variant="contained"
                                  className={classes.gridButton2}
                                  onClick={() => {
                                    handleClickPreview(values, errors, setTouched);
                                  }}
                                  id="prpman-preview-edit-button"
                                >
                                  {isPreviewSubmitting
                                    ? (<CircularProgress {...progressProps} />)
                                    : (<Typography class={classes.previewLabel} style={{ padding: 'unset' }}>{content.previewEditLabel || 'Preview'}</Typography>)}
                                </MuiButton>
                              )
                            }
                          </Box>
                        )
                      }

                    </Box>
                    {bannerShow1.open ? (
                      <Box
                        data-testid="banner-warp"
                        component="div"
                        className={classes.bannerWarp}
                      >
                        <Grid container className={classes.banner}>
                          <Grid key="4" item xs={1} className={classes.bannerIcon}>
                            <Box>{iconMap.Confirmation || infoIcon}</Box>
                          </Grid>
                          <Grid key="5" item xs={10} className={classes.titleWarp}>
                            <Typography>{bannerShow1.content}</Typography>
                          </Grid>
                          <Grid key="6" item xs={1} className={classes.closeIcon}>
                            <IconButton
                              size="small"
                              aria-label="close"
                              id="admin-banner-handleDismiss1"
                              name="admin-banner-handleDismiss1"
                              onClick={handleDismiss1}
                            >
                              <CloseIcon
                                fontSize="small"
                                color="secondary"
                              />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </Box>
                    ) : null}
                  </Box>
                  {
                    isWebApp && (
                      <Box style={{
                        marginTop: 24,
                        height: 62,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                      >
                        {market === 'JT' ? justineLogo : avonLogo}
                      </Box>
                    )
                  }
                  <Box style={isWebApp ? {
                    display: 'flex',
                    flexDirection: 'row-reverse',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '18px 24px 18px',
                  } : {}}
                  >
                    {hasProfile && (
                      <Box component="div" className={classes.switchWarp} flex={1}>
                        <Grid container id="admin-prpman-container-2">
                          <Grid key="1" item xs={12} className={classes.switchLabel1}>
                            <Box display="flex">
                              <Box
                                component="span"
                                ml={language === 'ar' ? 'unset' : 2}
                                mr={language === 'ar' ? 2 : 'unset'}
                                style={{ cursor: 'pointer' }}
                              >
                                <AvonTooltip
                                  position="bottom"
                                  trigger="click"
                                  arrow
                                  tipMsg={tipMsg}
                                  theme="light"
                                  interactive
                                  order="toggle"
                                  showDissmiss
                                  language={language}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                  >
                                    <g fill="none" fillRule="evenodd">
                                      <path
                                        d="M0 0H24V24H0z"
                                        transform="translate(-2 -2)"
                                      />
                                      <path
                                        fill={theme.palette.colors.corePink}
                                        fillRule="nonzero"
                                        d="M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 2c-4.418 0-8 3.582-8 8s3.582 8 8 8 8-3.582 8-8-3.582-8-8-8zm0 11c.552 0 1 .448 1 1s-.448 1-1 1-1-.448-1-1 .448-1 1-1zm0-8c1.656 0 3 1.345 3 3 0 .925-.383 1.542-1.164 2.265l-.57.507-.238.232c-.014.012-.022.015-.025.01L13 13.003l-.007.114c-.057.497-.48.883-.993.883-.552 0-1-.448-1-1 0-.643.296-1.114.829-1.622l.165-.152.343-.302c.5-.443.663-.678.663-.924 0-.55-.449-1-1-1-.513 0-.936.386-.993.883L11 10c0 .552-.448 1-1 1s-1-.448-1-1c0-1.657 1.343-3 3-3z"
                                        transform="translate(-2 -2)"
                                      />
                                    </g>
                                  </svg>
                                </AvonTooltip>
                              </Box>
                              <Box pl={language === 'ar' ? 'unset' : 2} pr={language === 'ar' ? 2 : 'unset'}>
                                {switchBarObject.label}
                              </Box>
                            </Box>
                            <Box ml={language === 'ar' ? 'unset' : 6} mr={language === 'ar' ? 6 : 'unset'}>
                              <Box style={{ transform: language === 'ar' ? 'rotateY(180deg)' : 'rotateY(0)' }}>
                                <IOSSwitch
                                  checked={!switchDisable}
                                  onChange={openSwitchModal}
                                  value="checkedB"
                                  id="admin-prpman-switchr"
                                  name="admin-prpman-switchr"
                                />
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    )}

                    {switchDisable && (
                      <Grid key="2" item xs={12} className={classes.switchLabel1}>
                        <Box display="flex">
                          <Box
                            component="span"
                            ml={language === 'ar' ? 'unset' : 2}
                            mr={language === 'ar' ? 2 : 'unset'}
                            style={{ cursor: 'pointer' }}
                          >
                            <AvonTooltip
                              position="bottom"
                              trigger="click"
                              arrow
                              tipMsg={oooTipMsg}
                              theme="light"
                              interactive
                              order="toggle"
                              showDissmiss
                              language={language}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                              >
                                <g fill="none" fillRule="evenodd">
                                  <path
                                    d="M0 0H24V24H0z"
                                    transform="translate(-2 -2)"
                                  />
                                  <path
                                    fill={theme.palette.colors.corePink}
                                    fillRule="nonzero"
                                    d="M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 2c-4.418 0-8 3.582-8 8s3.582 8 8 8 8-3.582 8-8-3.582-8-8-8zm0 11c.552 0 1 .448 1 1s-.448 1-1 1-1-.448-1-1 .448-1 1-1zm0-8c1.656 0 3 1.345 3 3 0 .925-.383 1.542-1.164 2.265l-.57.507-.238.232c-.014.012-.022.015-.025.01L13 13.003l-.007.114c-.057.497-.48.883-.993.883-.552 0-1-.448-1-1 0-.643.296-1.114.829-1.622l.165-.152.343-.302c.5-.443.663-.678.663-.924 0-.55-.449-1-1-1-.513 0-.936.386-.993.883L11 10c0 .552-.448 1-1 1s-1-.448-1-1c0-1.657 1.343-3 3-3z"
                                    transform="translate(-2 -2)"
                                  />
                                </g>
                              </svg>
                            </AvonTooltip>
                          </Box>
                          <Box pl={language === 'ar' ? 'unset' : 2} pr={language === 'ar' ? 2 : 'unset'}>
                            {switchBarObject.oooLabel || 'Out Of Office'}
                          </Box>
                        </Box>
                        <Box ml={language === 'ar' ? 'unset' : 6} mr={language === 'ar' ? 6 : 'unset'}>
                          <Box style={{ transform: language === 'ar' ? 'rotateY(180deg)' : 'rotateY(0)' }}>
                            <IOSSwitch
                              checked={!oooDisable}
                              onChange={toggleOoo}
                              value="checkedB"
                              id="admin-prpman-switchr"
                              name="admin-prpman-switchr"
                            />
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </Box>
                  {
                    !oooDisable && switchDisable ? (
                      <Box style={isWebApp ? {
                        display: 'flex',
                        justifyContent: 'space-between',
                        flexDirection: isWebApp && isTablet() ? 'column' : 'row',
                        padding: '0 24px 36px',
                        marginBottom: 24,
                        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                      } : {}}
                      >
                        {
                          isWebApp && (
                            <Box style={{
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              height: 58,
                              fontFamily: 'Montserrat',
                              marginRight: 24,
                              flex: 1,
                            }}
                            >
                              <Box style={{
                                fontWeight: 'bold',
                                fontSize: '20px',
                                lineHeight: '28px',
                                color: '#181818',
                              }}
                              >
                                {content.selectDateLabel}
                              </Box>
                              <Box style={{
                                fontSize: '14px',
                                lineHeight: '24px',
                                color: '#000000',
                              }}
                              >
                                {content.selectDateDesc}
                              </Box>
                            </Box>
                          )
                        }
                        <Box style={isWebApp ? {
                          flex: 1,
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: isWebApp && isDesktop() ? 'space-between' : 'flex-start',
                          marginTop: isWebApp && isDesktop() ? 34 : 12,
                        } : {}}
                        >
                          <Box style={isWebApp ? {
                            flex: isWebApp && isDesktop() ? 1 : 'unset',
                            width: isWebApp && isTablet() ? '50%' : 'unset',
                            marginRight: 20,
                            position: 'relative',
                          } : {
                            boxShadow: '0px 2px 4px #E1E1E1',
                            margin: 12,
                          }}
                          >
                            <Box
                              style={isWebApp ? {
                                position: 'relative',
                              } : {
                                textAlign: 'center',
                                padding: 16,
                              }}
                              onClick={toggleDateRangeShow}
                            >
                              <input id="oooCalendar" readOnly value={oooDateRangeLabel} className={showDateRangeError ? classes.oooCalendarError : classes.oooCalendar} />
                              <svg
                                style={{
                                  transform: dateRangeShow ? 'rotateZ(180deg)' : '',
                                  position: 'absolute',
                                  // eslint-disable-next-line
                                  right: language !== 'ar' ? (isUpMd ? 35 : 28) : 'unset',
                                  // eslint-disable-next-line
                                  left: language === 'ar' ? (isUpMd ? 35 : 0) : 'unset',
                                }}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path d="M12.707 5.2926C13.0676 5.65305 13.0953 6.22027 12.7904 6.6126L12.7072 6.70682L8.70762 10.7073C8.34715 11.0678 7.77987 11.0956 7.38755 10.7905L7.29333 10.7073L3.29289 6.70689C2.90237 6.31637 2.90237 5.6832 3.29289 5.29268C3.65338 4.9322 4.22061 4.90447 4.6129 5.20949L4.70711 5.29268L8.00122 8.58601L11.2928 5.29276C11.6533 4.93223 12.2205 4.90444 12.6128 5.20942L12.707 5.2926Z" fill="#181818" />
                              </svg>
                            </Box>
                            <Box style={{ textAlign: 'center' }}>
                              <Typography style={{
                                display: showDateRangeError ? 'block' : 'none',
                                textAlign: 'left',
                                marginLeft: isWebApp ? 0 : 32,
                                color: '#851168',
                                fontFamily: 'Montserrat',
                                fontWeight: 400,
                                fontSize: 12,
                                letterSpacing: -0.1,
                              }}
                              >
                                {switchBarObject.selectDateError || 'You need to select a date or range of date'}
                              </Typography>
                            </Box>
                            <Box
                              p={theme.spacing(1)}
                              style={isWebApp ? {
                                position: 'absolute',
                                textAlign: 'center',
                                display: dateRangeShow ? 'block' : 'none',
                                background: '#ffffff',
                                boxShadow: 'rgb(225 225 225) 0px 2px 4px 0px',
                                zIndex: 10,
                              } : {
                                textAlign: 'center',
                                display: dateRangeShow ? 'block' : 'none',
                              }}
                            >
                              <DatePicker
                                isRange
                                oooStart={oooStart}
                                oooEnd={oooEnd}
                                okLabel="DONE"
                                onChange={updateDateRange}
                                id="OooDate"
                                name="OooDate"
                                locale={language === 'ar' ? `${language}${market}` : language}
                              />
                            </Box>
                          </Box>
                          <Box
                            mb={isWebApp ? 0 : 5}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            p={isWebApp ? 'unset' : '0 94px'}
                            style={{
                              // eslint-disable-next-line
                              display: isWebApp ? 'flex' :
                                dateRangeShow && oooStart !== null && oooStart !== false ? 'flex' : 'none',
                              marginLeft: language === 'ar' ? 'unset' : 12,
                              marginRight: language === 'ar' ? 12 : 'unset',
                            }}
                          >
                            <Button
                              name="ooo-done"
                              // eslint-disable-next-line max-len
                              disabled={!(dateRangeShow && oooStart !== null && oooStart !== false)}
                              onClick={() => {
                                if (dateRangeShow && oooStart !== null && oooStart !== false) {
                                  if (!oooDisable && (oooStart === null || oooStart === false)) {
                                    setShowDateRangeError(true);
                                  } else {
                                    toggleDateRangeShow();
                                  }
                                }
                              }}
                              customizedStyle={{ minHeight: 32, marginBottom: isWebApp ? 'unset' : 8 }}
                            >
                              {switchBarObject.oooDateRangeDoneLabel || 'DONE'}
                            </Button>
                            <Button
                              priority="tertiary"
                              name="ooo-clear"
                              // eslint-disable-next-line max-len
                              disabled={!(oooStart !== null && oooStart !== false)}
                              onClick={oooStart ? toggleOoo : noop}
                              customizedStyle={{ minHeight: 32 }}
                            >
                              {switchBarObject.oooDateRangeClearLabel || 'CLEAR SELECTION'}
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    ) : null
                  }

                  {switchDisable ? (
                    <>
                      {
                        isWebApp ? (
                          <Box px={6} ref={inputRef}>
                            <WebHeading title={content.fields[1][0].properties.text} />
                            <Box style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                            >
                              <Box style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                marginRight: language === 'ar' ? 'unset' : 24,
                                marginLeft: language === 'ar' ? 24 : 'unset',
                              }}
                              >
                                <AvatarHeader
                                  avatarUrl={newImageUrl}
                                  alt="profile-icon"
                                  onEditClick={handleCapture}
                                  language={language}
                                  editButton
                                  onComplete={avatarImageUpdate}
                                  labels={{
                                    ...content.labels.editorLabels,
                                    cancel: content.labels.cancelLabel,
                                    save: content.labels.saveLabel,
                                    title: content.labels.editLabels.title,
                                  }}
                                  takePicture={content.takePicture}
                                  uploadPicture={content.uploadPicture}
                                  deletePicture={content.deletePicture}
                                  dragPicture={content.dragPicture}
                                  NoCameraFoundModal={content.NoCameraFoundModal}
                                  allowCameraModal={content.allowCameraModal}
                                  deleteImage={deleteImage}
                                  uploadAvatar={uploadAvatar}
                                  setEditLoading={setEditLoading}
                                />
                                <Box id={`${content.fields[0][0].properties.name}`}>
                                  <Field
                                    // onChange={e => inputChange(e, handleChange)}
                                    // setIsDataChange={setIsDataChange}
                                    onBlur={e => getChangeContent(e, handleBlur)}
                                    onFocus={focusCheck}
                                    focusOn={focusOn}
                                    callApi={callApi}
                                    isAvailableURL={isAvailableURL}
                                    setIsAvailableURL={setIsAvailableURL}
                                    component={components.InputField}
                                    locationShow={locationShowBool}
                                    setLocationShow={setLocationShow}
                                    phoneShow={phoneShowBool}
                                    setPhoneShow={setPhoneShow}
                                    suggestList={suggestList1}
                                    emailShow={emailShowBool}
                                    setEmailShow={setEmailShow}
                                    aboutMeShow={aboutMeShowBool}
                                    setAboutMeShow={setAboutMeShow}
                                    introShow={introShowBool}
                                    setIntroShow={setIntroShow}
                                    showHideMap={content.showHideMap}
                                    setBannerShow1={setBannerShow1}
                                    setPreviewShow={setPreviewShow}
                                    language={language}
                                    {...content.fields[0][0].properties}
                                  />
                                </Box>
                              </Box>
                              <Box flex={1}>
                                <Grid container spacing={7}>
                                  {
                                    content.fields[1].filter(({ type }) => type !== 'Heading').map(({ type, properties }, item) => (
                                      <Grid
                                        item
                                        xs={12}
                                        key={`${item}-${properties.name}`}
                                        id={`${item}-${properties.name}`}
                                        className={properties.showBgColor
                                          ? classes.fieldBgColor : classes.fieldWarp}
                                      >
                                        <Field
                                          // onChange={e => inputChange(e, handleChange)}
                                          // setIsDataChange={setIsDataChange}
                                          onBlur={e => getChangeContent(e, handleBlur)}
                                          onFocus={focusCheck}
                                          focusOn={focusOn}
                                          callApi={callApi}
                                          isAvailableURL={isAvailableURL}
                                          setIsAvailableURL={setIsAvailableURL}
                                          component={components[type]}
                                          locationShow={locationShowBool}
                                          setLocationShow={setLocationShow}
                                          phoneShow={phoneShowBool}
                                          setPhoneShow={setPhoneShow}
                                          suggestList={suggestList1}
                                          emailShow={emailShowBool}
                                          setEmailShow={setEmailShow}
                                          aboutMeShow={aboutMeShowBool}
                                          setAboutMeShow={setAboutMeShow}
                                          introShow={introShowBool}
                                          setIntroShow={setIntroShow}
                                          showHideMap={content.showHideMap}
                                          setBannerShow1={setBannerShow1}
                                          setPreviewShow={setPreviewShow}
                                          language={language}
                                          {...properties}
                                        />
                                      </Grid>
                                    ))
                                  }
                                </Grid>
                              </Box>
                            </Box>
                            <Box className={classes.divider} />
                            <WebHeading
                              title={content.fields[2][0].properties.text}
                              description={content.fields[2][1].properties.text}
                              id="WebHeading"
                            />
                            {
                              content.fields[2].filter(({ type }) => type === 'Dropdown').length !== 0 ? (
                                <>
                                  <Grid
                                    container
                                    spacing={6}
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'flex-start',
                                    }}
                                  >
                                    {
                                      // eslint-disable-next-line
                                      content.fields[2].filter(({ }, index) => index >= 2 && index <= 3).map(({ type, properties }, item) => (
                                        <Grid
                                          item
                                          xs={type === 'Dropdown' ? 2 : 4}
                                          key={`${item}-${properties.name}`}
                                          id={`${item}-${properties.name}`}
                                          className={properties.showBgColor
                                            ? classes.fieldBgColor : classes.fieldWarp}
                                          style={(item === 0 && type !== 'Dropdown') ? {
                                            paddingTop: 28,
                                          } : {}}
                                        >
                                          <Field
                                            // onChange={e => inputChange(e, handleChange)}
                                            // setIsDataChange={setIsDataChange}
                                            onBlur={e => getChangeContent(e, handleBlur)}
                                            onFocus={focusCheck}
                                            focusOn={focusOn}
                                            callApi={callApi}
                                            isAvailableURL={isAvailableURL}
                                            setIsAvailableURL={setIsAvailableURL}
                                            component={components[type]}
                                            locationShow={locationShowBool}
                                            setLocationShow={setLocationShow}
                                            phoneShow={phoneShowBool}
                                            setPhoneShow={setPhoneShow}
                                            suggestList={suggestList1}
                                            emailShow={emailShowBool}
                                            setEmailShow={setEmailShow}
                                            aboutMeShow={aboutMeShowBool}
                                            setAboutMeShow={setAboutMeShow}
                                            introShow={introShowBool}
                                            setIntroShow={setIntroShow}
                                            showHideMap={content.showHideMap}
                                            setBannerShow1={setBannerShow1}
                                            setPreviewShow={setPreviewShow}
                                            language={language}
                                            restoreValue={restoreValue}
                                            editPhone={editPhone}
                                            editEmail={editEmail}
                                            initialData={initialData}
                                            {...properties}
                                            disabled={!editPhone}
                                          />
                                          <div id="web-saveButton" style={{ display: type === 'Dropdown' ? 'block' : 'none', position: 'relative', top: 12 }}>
                                            <EditSaveButton
                                              saveLabel={content.labels.saveLabel}
                                              cancelLabel={content.labels.cancelLabel}
                                              editLabel={content.testimonialContent.editIconLabel}
                                              type="phone"
                                              values={values}
                                              savePhoneEmail={savePhoneEmail}
                                              setRestoreValue={setRestoreValue}
                                              language={language}
                                              edit={editPhone}
                                              isSubmitting={isSubmitting}
                                              setEditPhone={setEditPhone}
                                              setEditEmail={setEditEmail}
                                            />
                                          </div>
                                        </Grid>
                                      ))
                                    }
                                    {
                                      // eslint-disable-next-line
                                      content.fields[2].filter(({ type }, index) => index === 4 && type === 'InputField').map(({ type, properties }, item) => (
                                        <Grid
                                          item
                                          xs={6}
                                          key={`${item}-${properties.name}`}
                                          id={`${item}-${properties.name}`}
                                          className={properties.showBgColor
                                            ? classes.fieldBgColor : classes.fieldWarp}
                                        >
                                          <Field
                                            // onChange={e => inputChange(e, handleChange)}
                                            // setIsDataChange={setIsDataChange}
                                            onBlur={e => getChangeContent(e, handleBlur)}
                                            onFocus={focusCheck}
                                            focusOn={focusOn}
                                            callApi={callApi}
                                            isAvailableURL={isAvailableURL}
                                            setIsAvailableURL={setIsAvailableURL}
                                            component={components[type]}
                                            locationShow={locationShowBool}
                                            setLocationShow={setLocationShow}
                                            phoneShow={phoneShowBool}
                                            setPhoneShow={setPhoneShow}
                                            suggestList={suggestList1}
                                            emailShow={emailShowBool}
                                            setEmailShow={setEmailShow}
                                            aboutMeShow={aboutMeShowBool}
                                            setAboutMeShow={setAboutMeShow}
                                            introShow={introShowBool}
                                            setIntroShow={setIntroShow}
                                            showHideMap={content.showHideMap}
                                            setBannerShow1={setBannerShow1}
                                            setPreviewShow={setPreviewShow}
                                            language={language}
                                            restoreValue={restoreValue}
                                            editPhone={editPhone}
                                            editEmail={editEmail}
                                            initialData={initialData}
                                            {...properties}
                                            disabled={!editEmail}
                                          />
                                          <EditSaveButton
                                            saveLabel={content.labels.saveLabel}
                                            cancelLabel={content.labels.cancelLabel}
                                            editLabel={content.testimonialContent.editIconLabel}
                                            type="email"
                                            values={values}
                                            savePhoneEmail={savePhoneEmail}
                                            setRestoreValue={setRestoreValue}
                                            language={language}
                                            edit={editEmail}
                                            isSubmitting={isSubmitting}
                                            setEditPhone={setEditPhone}
                                            setEditEmail={setEditEmail}
                                          />
                                        </Grid>
                                      ))
                                    }
                                  </Grid>
                                </>
                              ) : (
                                <Grid
                                  container
                                  spacing={7}
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  {
                                    // eslint-disable-next-line
                                    content.fields[2].filter(({ type }, index) => index >= 2 && index <= 3 && type === 'InputField').map(({ type, properties }, item) => (
                                      <Grid
                                        item
                                        xs={6}
                                        key={`${item}-${properties.name}`}
                                        id={`${item}-${properties.name}`}
                                        className={properties.showBgColor
                                          ? classes.fieldBgColor : classes.fieldWarp}
                                      >
                                        <Field
                                          // onChange={e => inputChange(e, handleChange)}
                                          // setIsDataChange={setIsDataChange}
                                          onBlur={e => getChangeContent(e, handleBlur)}
                                          onFocus={focusCheck}
                                          focusOn={focusOn}
                                          callApi={callApi}
                                          isAvailableURL={isAvailableURL}
                                          setIsAvailableURL={setIsAvailableURL}
                                          component={components[type]}
                                          locationShow={locationShowBool}
                                          setLocationShow={setLocationShow}
                                          phoneShow={phoneShowBool}
                                          setPhoneShow={setPhoneShow}
                                          suggestList={suggestList1}
                                          emailShow={emailShowBool}
                                          setEmailShow={setEmailShow}
                                          aboutMeShow={aboutMeShowBool}
                                          setAboutMeShow={setAboutMeShow}
                                          introShow={introShowBool}
                                          setIntroShow={setIntroShow}
                                          showHideMap={content.showHideMap}
                                          setBannerShow1={setBannerShow1}
                                          setPreviewShow={setPreviewShow}
                                          language={language}
                                          restoreValue={restoreValue}
                                          editPhone={editPhone}
                                          editEmail={editEmail}
                                          initialData={initialData}
                                          {...properties}
                                          // eslint-disable-next-line
                                          disabled={properties.name === 'mobilePhonNr' && !editPhone ? true : (properties.name === 'emailAddrTxt' && !editEmail) ? true : false}
                                        />
                                        <div
                                          id={`web-saveButton-${properties.name}`}
                                          style={{
                                            display: properties.name === 'mobilePhonNr' || properties.name === 'emailAddrTxt'
                                              ? 'block' : 'none',
                                            marginTop: 14,
                                          }}
                                        >
                                          <EditSaveButton
                                            saveLabel={content.labels.saveLabel}
                                            cancelLabel={content.labels.cancelLabel}
                                            editLabel={content.testimonialContent.editIconLabel}
                                            type={properties.name === 'mobilePhonNr' ? 'phone' : 'email'}
                                            values={values}
                                            savePhoneEmail={savePhoneEmail}
                                            setRestoreValue={setRestoreValue}
                                            language={language}
                                            edit={properties.name === 'mobilePhonNr' ? editPhone : editEmail}
                                            isSubmitting={isSubmitting}
                                            setEditPhone={setEditPhone}
                                            setEditEmail={setEditEmail}
                                          />
                                        </div>
                                      </Grid>
                                    ))
                                  }
                                </Grid>
                              )
                            }

                            <Grid container spacing={7}>
                              {
                                // eslint-disable-next-line
                                content.fields[2].filter(({ type }) => type === 'SocialList').map(({ type, properties }, item) => (
                                  <Grid
                                    item
                                    key={`${item}-${properties.name}`}
                                    id={`${item}-${properties.name}`}
                                    className={properties.showBgColor
                                      ? classes.fieldBgColor : classes.fieldWarp}
                                  >
                                    <Field
                                      // onChange={e => inputChange(e, handleChange)}
                                      // setIsDataChange={setIsDataChange}
                                      onBlur={e => getChangeContent(e, handleBlur)}
                                      onFocus={focusCheck}
                                      focusOn={focusOn}
                                      callApi={callApi}
                                      isAvailableURL={isAvailableURL}
                                      setIsAvailableURL={setIsAvailableURL}
                                      component={components[type]}
                                      locationShow={locationShowBool}
                                      setLocationShow={setLocationShow}
                                      phoneShow={phoneShowBool}
                                      setPhoneShow={setPhoneShow}
                                      suggestList={suggestList1}
                                      emailShow={emailShowBool}
                                      setEmailShow={setEmailShow}
                                      aboutMeShow={aboutMeShowBool}
                                      setAboutMeShow={setAboutMeShow}
                                      introShow={introShowBool}
                                      setIntroShow={setIntroShow}
                                      showHideMap={content.showHideMap}
                                      setBannerShow1={setBannerShow1}
                                      setPreviewShow={setPreviewShow}
                                      language={language}
                                      {...properties}
                                    />
                                  </Grid>
                                ))
                              }
                            </Grid>

                            <Grid container>
                              {
                                // eslint-disable-next-line
                                content.fields[2].filter((o, index) => index === content.fields[2].length - 1).map(({ type, properties }, item) => (
                                  <Grid
                                    item
                                    key={`${item}-${properties.name}`}
                                    id={`${item}-${properties.name}`}
                                    className={`${properties.showBgColor ? classes.fieldBgColor : classes.fieldWarp} ${isWebApp ? classes.webVanityName : ''}`}
                                    style={{
                                      padding: '24px 0',
                                    }}
                                  >
                                    <Field
                                      // onChange={e => inputChange(e, handleChange)}
                                      // setIsDataChange={setIsDataChange}
                                      onBlur={e => getChangeContent(e, handleBlur)}
                                      onFocus={focusCheck}
                                      focusOn={focusOn}
                                      callApi={callApi}
                                      isAvailableURL={isAvailableURL}
                                      setIsAvailableURL={setIsAvailableURL}
                                      component={components[type]}
                                      locationShow={locationShowBool}
                                      setLocationShow={setLocationShow}
                                      phoneShow={phoneShowBool}
                                      setPhoneShow={setPhoneShow}
                                      suggestList={suggestList1}
                                      emailShow={emailShowBool}
                                      setEmailShow={setEmailShow}
                                      aboutMeShow={aboutMeShowBool}
                                      setAboutMeShow={setAboutMeShow}
                                      introShow={introShowBool}
                                      setIntroShow={setIntroShow}
                                      showHideMap={content.showHideMap}
                                      setBannerShow1={setBannerShow1}
                                      setPreviewShow={setPreviewShow}
                                      language={language}
                                      {...properties}
                                    />
                                  </Grid>
                                ))
                              }
                            </Grid>
                            <Box className={classes.divider} />
                            <WebHeading
                              title={content.fields[3][0].properties.text}
                              description={content.fields[3][1].properties.text}
                            />
                            <Grid
                              container
                              spacing={7}
                              style={{
                                display: 'block',
                              }}
                            >
                              {
                                // eslint-disable-next-line
                                content.fields[3].filter(({ type }) => type !== 'Heading').map(({ type, properties }, item) => (
                                  <Grid
                                    item
                                    key={`${item}-${properties.name}`}
                                    id={`${item}-${properties.name}`}
                                    className={properties.showBgColor
                                      ? classes.fieldBgColor : classes.fieldWarp}
                                  >
                                    <Field
                                      // onChange={e => inputChange(e, handleChange)}
                                      // setIsDataChange={setIsDataChange}
                                      onBlur={e => getChangeContent(e, handleBlur)}
                                      onFocus={focusCheck}
                                      focusOn={focusOn}
                                      callApi={callApi}
                                      isAvailableURL={isAvailableURL}
                                      setIsAvailableURL={setIsAvailableURL}
                                      component={components[type]}
                                      locationShow={locationShowBool}
                                      setLocationShow={setLocationShow}
                                      phoneShow={phoneShowBool}
                                      setPhoneShow={setPhoneShow}
                                      suggestList={suggestList1}
                                      emailShow={emailShowBool}
                                      setEmailShow={setEmailShow}
                                      aboutMeShow={aboutMeShowBool}
                                      setAboutMeShow={setAboutMeShow}
                                      introShow={introShowBool}
                                      setIntroShow={setIntroShow}
                                      showHideMap={content.showHideMap}
                                      setBannerShow1={setBannerShow1}
                                      setPreviewShow={setPreviewShow}
                                      language={language}
                                      {...properties}
                                    />
                                  </Grid>
                                ))
                              }
                            </Grid>
                            <Box className={classes.divider} />
                            <WebHeading
                              title={content.fields[4][0].properties.text}
                              description={content.fields[4][1].properties.text}
                            />
                          </Box>
                        ) : (
                          <Box pt={3.5} ref={inputRef}>
                            <AvatarHeader
                              // title={content.labels.avatarTitle}
                              avatarUrl={newImageUrl}
                              alt="profile-icon"
                              onEditClick={handleCapture}
                              language={language}
                              editButton
                              onComplete={avatarImageUpdate}
                              labels={{
                                ...content.labels.editorLabels,
                                cancel: content.labels.cancelLabel,
                                save: content.labels.saveLabel,
                                title: content.labels.editLabels.title,
                              }}
                              takePicture={content.takePicture}
                              uploadPicture={content.uploadPicture}
                              deletePicture={content.deletePicture}
                              dragPicture={content.dragPicture}
                              NoCameraFoundModal={content.NoCameraFoundModal}
                              allowCameraModal={content.allowCameraModal}
                              deleteImage={deleteImage}
                              uploadAvatar={uploadAvatar}
                              setEditLoading={setEditLoading}
                            />
                            {content.fields.map((section, i) => (
                              <ContentWrapper
                                key={`form-section-${i}`}
                                id={`form-section-${i}`}
                              >
                                <Grid container spacing={7}>
                                  {section.map(({ type, properties }, item) => (
                                    // mobile mobilePhonNrCd & mobilePhonNr
                                    <>
                                      {i === 2 && item >= 2 && item <= 3 && (properties.name === 'mobilePhonNrCd' || properties.name === 'mobilePhonNr')
                                        ? (
                                          <Grid
                                            item
                                            // eslint-disable-next-line
                                            xs={hasMobileCd ? type === 'Dropdown' ? 5 : 7 : 12}
                                            key={`${item}-${properties.name}`}
                                            id={`${item}-${properties.name}`}
                                            className={properties.showBgColor
                                              ? classes.fieldBgColor : classes.fieldWarp}
                                            style={(item === 0 && type !== 'Dropdown') ? {
                                              paddingTop: 28,
                                            } : {}}
                                          >
                                            <Field
                                              // onChange={e => inputChange(e, handleChange)}
                                              // setIsDataChange={setIsDataChange}
                                              onBlur={e => getChangeContent(e, handleBlur)}
                                              onFocus={focusCheck}
                                              focusOn={focusOn}
                                              callApi={callApi}
                                              isAvailableURL={isAvailableURL}
                                              setIsAvailableURL={setIsAvailableURL}
                                              component={components[type]}
                                              locationShow={locationShowBool}
                                              setLocationShow={setLocationShow}
                                              phoneShow={phoneShowBool}
                                              setPhoneShow={setPhoneShow}
                                              suggestList={suggestList1}
                                              emailShow={emailShowBool}
                                              setEmailShow={setEmailShow}
                                              aboutMeShow={aboutMeShowBool}
                                              setAboutMeShow={setAboutMeShow}
                                              introShow={introShowBool}
                                              setIntroShow={setIntroShow}
                                              showHideMap={content.showHideMap}
                                              setBannerShow1={setBannerShow1}
                                              setPreviewShow={setPreviewShow}
                                              language={language}
                                              restoreValue={restoreValue}
                                              editPhone={editPhone}
                                              editEmail={editEmail}
                                              initialData={initialData}
                                              {...properties}
                                              disabled={!editPhone}
                                            />
                                          </Grid>
                                        )
                                        : (
                                          <Grid
                                            item
                                            xs={12}
                                            key={`${item}-${properties.name}`}
                                            id={`${item}-${properties.name}`}
                                            className={properties.showBgColor
                                              ? classes.fieldBgColor : classes.fieldWarp}
                                          >
                                            <div id="mobile-editphone" style={{ display: properties.name === 'emailAddrTxt' ? 'block' : 'none', position: 'relative', top: '-16px' }}>
                                              <EditSaveButton
                                                saveLabel={content.labels.saveLabel}
                                                cancelLabel={content.labels.cancelLabel}
                                                editLabel={content.testimonialContent.editIconLabel}
                                                type="phone"
                                                values={values}
                                                savePhoneEmail={savePhoneEmail}
                                                setRestoreValue={setRestoreValue}
                                                language={language}
                                                edit={editPhone}
                                                isSubmitting={isSubmitting}
                                                setEditPhone={setEditPhone}
                                                setEditEmail={setEditEmail}
                                              />
                                            </div>
                                            <Field
                                              // onChange={e => inputChange(e, handleChange)}
                                              // setIsDataChange={setIsDataChange}
                                              onBlur={e => getChangeContent(e, handleBlur)}
                                              onFocus={focusCheck}
                                              focusOn={focusOn}
                                              callApi={callApi}
                                              isAvailableURL={isAvailableURL}
                                              setIsAvailableURL={setIsAvailableURL}
                                              component={components[type]}
                                              locationShow={locationShowBool}
                                              setLocationShow={setLocationShow}
                                              phoneShow={phoneShowBool}
                                              setPhoneShow={setPhoneShow}
                                              suggestList={suggestList1}
                                              emailShow={emailShowBool}
                                              setEmailShow={setEmailShow}
                                              aboutMeShow={aboutMeShowBool}
                                              setAboutMeShow={setAboutMeShow}
                                              introShow={introShowBool}
                                              setIntroShow={setIntroShow}
                                              showHideMap={content.showHideMap}
                                              setBannerShow1={setBannerShow1}
                                              setPreviewShow={setPreviewShow}
                                              language={language}
                                              restoreValue={restoreValue}
                                              editPhone={editPhone}
                                              editEmail={editEmail}
                                              initialData={initialData}
                                              {...properties}
                                              // eslint-disable-next-line
                                              disabled={properties.name === 'mobilePhonNr' || properties.name === 'mobilePhonNrCd' && !editPhone ? true : (properties.name == 'emailAddrTxt' && !editEmail) ? true : false}
                                            />
                                            <div id="mobile-editEmail" style={{ display: properties.name === 'emailAddrTxt' ? 'block' : 'none' }}>
                                              <EditSaveButton
                                                saveLabel={content.labels.saveLabel}
                                                cancelLabel={content.labels.cancelLabel}
                                                editLabel={content.testimonialContent.editIconLabel}
                                                type="email"
                                                values={values}
                                                savePhoneEmail={savePhoneEmail}
                                                setRestoreValue={setRestoreValue}
                                                language={language}
                                                edit={editEmail}
                                                isSubmitting={isSubmitting}
                                                setEditPhone={setEditPhone}
                                                setEditEmail={setEditEmail}
                                              />
                                            </div>
                                          </Grid>
                                        )
                                      }
                                    </>
                                  ))}
                                  {i === content.fields.length - 1 ? (
                                    <Grid item xs={12}>
                                      <Box mb={3} display="none">
                                        <Button
                                          id="admin-prpman-submit-form"
                                          name="admin-prpman-submit-form"
                                          onClick={submitForm}
                                          priority="primary"
                                        >
                                          {content.labels.saveLabel}
                                        </Button>
                                      </Box>
                                    </Grid>
                                  ) : null}
                                </Grid>
                              </ContentWrapper>
                            ))}
                          </Box>
                        )
                      }
                      <Modal
                        {...content.modals.submitModal}
                        submitData={async () => {
                          submitData(values);
                        }}
                        isSubmitting={isSubmitting}
                        open={!!modalSubmit}
                        closeModal={closeSubmitModal}
                      />
                      <Modal
                        {...content.testimonialContent.modals.editModal}
                        icon
                        color={{
                          borderColor: 'rgba(240, 159, 135, 1)',
                          fontColor: 'rgba(240, 159, 135, 1)',
                        }}
                        submitData={handleEditFormSubmit}
                        isSubmitting={isSubmitting}
                        open={!!modalEdit}
                        closeModal={closeEditModal}
                      />
                      <Modal
                        {...content.testimonialContent.modals.delModal}
                        icon
                        color={{
                          borderColor: 'rgba(240, 159, 135, 1)',
                          fontColor: 'rgba(240, 159, 135, 1)',
                        }}
                        submitData={handleTestimonialFormDel}
                        isSubmitting={isSubmitting}
                        open={!!modalDel}
                        closeModal={closeDelModal}
                      />
                      <Modal
                        {...content.modals.backModal}
                        submitData={async () => {
                          if (errors && Object.keys(errors).length === 0) {
                            const sendRes = await submitData(values);
                            setModalBack(false);
                            return sendRes || { hasError: true };
                          }
                          setModalBack(false);
                          locateError(errors, setTouched);
                          return { hasError: true };
                        }}
                        isSubmitting={isSubmitting}
                        open={!!modalBack}
                        closeModal={backHome}
                        icon
                      />
                      <NewModal
                        {...content.modals.changeURLModal}
                        type="save"
                        open={!!errorModalOpen}
                        submitData={async () => submitData(values)}
                        isSubmitting={isSubmitting}
                        API_URL={apiURLLink || window.location.host}
                        closeChangeModal={() => setErrorModalOpen(false)}
                        vanityNameToChange={vanityNameString}
                        language={language}
                        errorType="error"
                      />
                      <NewModal
                        {...content.modals.editURLModal}
                        type="save"
                        open={!!editModalOpen}
                        submitData={async () => submitData(values)}
                        isSubmitting={isSubmitting}
                        API_URL={apiURLLink || window.location.host}
                        closeChangeModal={() => setEditModalOpen(false)}
                        vanityNameToChange={relVanityName}
                        errorType="error"
                        editModalOpen={editModalOpen}
                        language={language}
                      />
                      <Editor
                        editorImage={editorImage}
                        open={editLoading}
                        closeModal={setEditLoading}
                        setEditorImage={setEditorImage}
                        editLoading={editLoading}
                        setEditLoading={setEditLoading}
                        onComplete={avatarImageUpdate}
                        labels={{
                          ...content.labels.editorLabels,
                          cancel: content.labels.cancelLabel,
                          save: content.labels.saveLabel,
                          title: content.labels.editLabels.title,
                        }}
                      />
                      <Grid item xs={12}>
                        <Box px={6} py={5} bgcolor="colors.white">
                          <Testimonial
                            testimonialData={testimonials || []}
                            testimonialContent={content.testimonialContent}
                            language={language}
                            callApi={callApi}
                            isSubmitting={isSubmitting}
                            setBannerShow={setBannerShow1}
                            refresh={() => {
                              getData(true);
                            }}
                            onSaveClick={onSaveClick}
                            onDeleteClick={onDeleteClick}
                          />
                        </Box>
                      </Grid>
                    </>
                  ) : (
                    <Box pl={3} pr={3} pt={3.5}>
                      <Typography dangerouslySetInnerHTML={{ __html: description }} />
                    </Box>
                  )}
                </form>
              )}
            </Formik>
          </>
        )}
        <NewModal
          {...content.modals.disableURLModal}
          type="error"
          open={!!disableModalOpen}
          submitData={async () => changeStatus()}
          isSubmitting={isSubmitting}
          API_URL={apiURLLink || window.location.host}
          closeChangeModal={() => setDisableModalOpen(false)}
          vanityNameToChange={vanityNameString}
          language={language}
          errorType="disable"
        />
        <NewModal
          {...content.modals.enableURLModal}
          type="error"
          open={!!enableModalOpen}
          submitData={async () => changeStatus()}
          isSubmitting={isSubmitting}
          API_URL={apiURLLink || window.location.host}
          closeChangeModal={() => setEnableModalOpen(false)}
          vanityNameToChange={vanityNameString}
          language={language}
          errorType="enable"
        />
        {content.modals.errorModal && (
          <Modal
            {...content.modals.errorModal}
            submitData={getData}
            isSubmitting={isSubmitting}
            open={!!dateErrorModal}
            closeModal={() => setDataErrorModal(false)}
          />
        )}
      </ProspectContext.Provider>
    </>
  );
};

PrpMan.propTypes = {
  callApi: func.isRequired,
  onComplete: func.isRequired,
  language: string,
  market: string,
  backToEdit: bool,
};

ContentWrapper.propTypes = {
  children: node.isRequired,
  fill: bool,
};

export default PrpMan;
